import { pgTable, text, serial, integer, boolean, timestamp, jsonb, bigint, numeric, uniqueIndex } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// === SESSION (managed by connect-pg-simple, defined here so drizzle doesn't drop it) ===
export const session = pgTable("session", {
  sid: text("sid").primaryKey(),
  sess: jsonb("sess").notNull(),
  expire: timestamp("expire", { precision: 6 }).notNull(),
});

// === PLATFORM SETTINGS ===
export const platformSettings = pgTable("platform_settings", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(),
  value: text("value").notNull(),
});

// === ORGANIZATIONS (multi-tenant) ===
export const organizations = pgTable("organizations", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  status: text("status").notNull().default("active"), // active, suspended, trial, trialing, payment_failed, canceled
  businessMode: text("business_mode").notNull().default("property_management"), // property_management, service_business, hybrid
  onboardingCompleted: boolean("onboarding_completed").default(false),
  webhookToken: text("webhook_token"),
  timezone: text("timezone").notNull().default("UTC"),
  icalSyncTimes: jsonb("ical_sync_times").$type<string[]>().default(["08:00", "20:00"]),
  logoUrl: text("logo_url"),
  defaultCheckInTime: text("default_check_in_time").default("15:00"),
  defaultCheckOutTime: text("default_check_out_time").default("11:00"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeConnectedAccountId: text("stripe_connected_account_id"),
  stripeConnectOnboardingStatus: text("stripe_connect_onboarding_status").notNull().default("not_started"),
  marketplaceCommissionPct: numeric("marketplace_commission_pct", { precision: 5, scale: 4 }).notNull().default("0.10"),
  orgType: text("org_type"), // service_org, property_management_org, etc.
  subscriptionId: text("subscription_id"),
  subscriptionStatus: text("subscription_status"), // trialing, active, past_due, canceled, unpaid
  trialEndsAt: timestamp("trial_ends_at"),
  weekStartDay: integer("week_start_day").notNull().default(0), // 0=Sunday, 1=Monday, ..., 6=Saturday
  calendarStartHour: integer("calendar_start_hour").notNull().default(7),
  calendarEndHour: integer("calendar_end_hour").notNull().default(18),
  createdAt: timestamp("created_at").defaultNow(),
  // Auto-invoice generation config
  autoInvoiceEnabled: boolean("auto_invoice_enabled").notNull().default(false),
  autoInvoicePeriod: text("auto_invoice_period"), // 'monthly' | 'biweekly' | 'quarterly' | 'weekly'
  autoInvoiceDayOfMonth: integer("auto_invoice_day_of_month"), // 1-28; only for monthly/quarterly
  autoInvoiceDayOfWeek: integer("auto_invoice_day_of_week"), // 0-6 (0=Sun); only for weekly/biweekly
  autoInvoiceLastRunAt: timestamp("auto_invoice_last_run_at"),
});

export const insertOrganizationSchema = createInsertSchema(organizations).omit({ id: true, createdAt: true });

// === PM CLIENT RULES (default rules for a property manager/owner) ===
export type PmClientRules = {
  defaultCheckoutTime?: string;       // e.g. "11:00"
  cleaningNotes?: string;             // default instructions for cleaners
  notifyOnCheckout?: boolean;         // send notification when checkout submission arrives
  notifyOnCleaningComplete?: boolean; // send notification when cleaning job is done
  requireFobReturn?: boolean;         // default fob-return requirement
  defaultPriority?: string;           // "low" | "normal" | "urgent"
  additionalNotes?: string;           // any other notes for this PM
};

// === ADDITIONAL CONTACT (per-PM user) ===
export type AdditionalContact = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  label?: string;
  notify: {
    invoices: boolean;
    reservations: boolean;
    cleaning: boolean;
    general: boolean;
  };
};

// === UNIT RULES (per-apartment overrides) ===
export type UnitRules = {
  checkoutTime?: string;
  cleaningNotes?: string;
  notifyOnCheckout?: boolean;
  notifyOnCleaningComplete?: boolean;
  requireFobReturn?: boolean;
  defaultPriority?: string;
  additionalNotes?: string;
};

// === USERS ===
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  role: text("role").notNull().default("owner"), // platform_admin, super_admin, staff, owner, cleaner, maintenance
  organizationId: integer("organization_id").references(() => organizations.id),
  fullName: text("full_name"),
  email: text("email"),
  phone: text("phone"),
  isAdmin: boolean("is_admin").default(true),
  disabledFeatures: text("disabled_features").array().default([]),
  pmClientRules: jsonb("pm_client_rules").$type<PmClientRules>(),
  color: text("color"),
  additionalContacts: jsonb("additional_contacts").$type<AdditionalContact[]>(),
  showFinancesInWorkerView: boolean("show_finances_in_worker_view").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
});

export const insertUserSchema = createInsertSchema(users).omit({ id: true });

// === BUILDINGS ===
export const buildings = pgTable("buildings", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id),
  name: text("name").notNull(),
  address: text("address").notNull(),
  description: text("description"),
});

export const insertBuildingSchema = createInsertSchema(buildings).omit({ id: true });

// === APARTMENTS ===
export const apartments = pgTable("apartments", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id),
  buildingId: integer("building_id").references(() => buildings.id),
  ownerUserId: integer("owner_user_id").references(() => users.id),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  publicToken: text("public_token").notNull(),
  wifiSsid: text("wifi_ssid"),
  wifiPassword: text("wifi_password"),
  checkoutTime: text("checkout_time"),
  fobInstructions: text("fob_instructions"),
  heroImage: text("hero_image"),
  fobRequired: boolean("fob_required").default(true),
  bedrooms: integer("bedrooms").default(1),
  bathrooms: integer("bathrooms").default(1),
  maxGuests: integer("max_guests").default(2),
  location: text("location"),
  latitude: numeric("latitude", { precision: 10, scale: 7 }),
  longitude: numeric("longitude", { precision: 10, scale: 7 }),
  marketplaceVisible: boolean("marketplace_visible").default(false),
  marketplaceDescription: text("marketplace_description"),
  marketplacePrice: numeric("marketplace_price", { precision: 10, scale: 2 }),
  marketplaceFeatured: boolean("marketplace_featured").default(false),
  marketplaceFeaturedUntil: timestamp("marketplace_featured_until"),
  propertyType: text("property_type").default("apartment"),
  amenities: text("amenities"),
  aliases: text("aliases"),
  icalUrl: text("ical_url"),
  lastSyncedAt: timestamp("last_synced_at"),
  lastSyncResult: jsonb("last_sync_result").$type<{ created: number; updated: number; cancelled: number; errors: string[] } | null>(),
  customCleaningFee: numeric("custom_cleaning_fee", { precision: 10, scale: 2 }),
  customCleanerPay: numeric("custom_cleaner_pay", { precision: 10, scale: 2 }),
  unitRules: jsonb("unit_rules").$type<UnitRules>(),
  cleaningSupplyOverrides: jsonb("cleaning_supply_overrides").$type<number[] | null>(),
  laundryItems: jsonb("laundry_items").$type<{ name: string; defaultQty: number }[] | null>(),
  taxRateOverride: numeric("tax_rate_override", { precision: 5, scale: 2 }),
  taxLabelOverride: text("tax_label_override"),
  marketplaceCleaningFee: numeric("marketplace_cleaning_fee", { precision: 10, scale: 2 }),
});

export const insertApartmentSchema = createInsertSchema(apartments).omit({ id: true });

export const apartmentOwners = pgTable("apartment_owners", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  role: text("role").default("owner"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertApartmentOwnerSchema = createInsertSchema(apartmentOwners).omit({ id: true, createdAt: true });
export type InsertApartmentOwner = z.infer<typeof insertApartmentOwnerSchema>;
export type ApartmentOwner = typeof apartmentOwners.$inferSelect;

export const apartmentPhotos = pgTable("apartment_photos", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  url: text("url").notNull(),
  caption: text("caption"),
  sortOrder: integer("sort_order").notNull().default(0),
  isCover: boolean("is_cover").notNull().default(false),
});

export const insertApartmentPhotoSchema = createInsertSchema(apartmentPhotos).omit({ id: true });
export type ApartmentPhoto = typeof apartmentPhotos.$inferSelect;
export type InsertApartmentPhoto = z.infer<typeof insertApartmentPhotoSchema>;

// === SECTIONS (CONTENT) ===
export const sections = pgTable("sections", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }),
  buildingId: integer("building_id").references(() => buildings.id),
  category: text("category").notNull(),
  titleEn: text("title_en").notNull(),
  titleEs: text("title_es").notNull(),
  summaryEn: text("summary_en"),
  summaryEs: text("summary_es"),
  contentEn: text("content_en"),
  contentEs: text("content_es"),
  order: integer("order").default(0),
});

export const insertSectionSchema = createInsertSchema(sections).omit({ id: true });

// === CHECKLIST ITEMS ===
export const checklistItems = pgTable("checklist_items", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  textEn: text("text_en").notNull(),
  textEs: text("text_es").notNull(),
  isRequired: boolean("is_required").default(true),
});

export const insertChecklistItemSchema = createInsertSchema(checklistItems).omit({ id: true });

// === PHOTO REQUIREMENTS ===
export const photoRequirements = pgTable("photo_requirements", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  labelEn: text("label_en").notNull(),
  labelEs: text("label_es").notNull(),
  instructionsEn: text("instructions_en"),
  instructionsEs: text("instructions_es"),
  minPhotos: integer("min_photos").default(1),
  isRequired: boolean("is_required").default(true),
});

export const insertPhotoRequirementSchema = createInsertSchema(photoRequirements).omit({ id: true });

// === RESERVATIONS ===
export const reservations = pgTable("reservations", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  guestName: text("guest_name").notNull(),
  guestEmail: text("guest_email"),
  guestPhone: text("guest_phone"),
  checkInDate: timestamp("check_in_date").notNull(),
  checkOutDate: timestamp("check_out_date").notNull(),
  status: text("status").notNull().default("confirmed"),
  notes: text("notes"),
  source: text("source").default("manual"),
  externalId: text("external_id"),
  createdBy: integer("created_by").references(() => users.id),
  paidAmount: numeric("paid_amount", { precision: 10, scale: 2 }),
  paymentMethod: text("payment_method"),
  paymentStatus: text("payment_status").default("pending"),
  paymentSource: text("payment_source"),
  taxAmount: numeric("tax_amount", { precision: 10, scale: 2 }),
  taxRate: numeric("tax_rate", { precision: 5, scale: 2 }),
  taxLabel: text("tax_label"),
  upsellTotal: numeric("upsell_total", { precision: 10, scale: 2 }),
  affiliateId: integer("affiliate_id").references(() => users.id),
  referralCode: text("referral_code"),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  lastSeenInFeedAt: timestamp("last_seen_in_feed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertReservationSchema = createInsertSchema(reservations).omit({ id: true, createdAt: true });

// === CLEANING JOBS (auto-created from checkout) ===
export const cleaningJobs = pgTable("cleaning_jobs", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }),
  submissionId: integer("submission_id").references(() => submissions.id, { onDelete: "set null" }),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  nextReservationId: integer("next_reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  assignedTo: integer("assigned_to").references(() => users.id),
  secondCleanerId: integer("second_cleaner_id").references(() => users.id, { onDelete: "set null" }),
  status: text("status").notNull().default("pending"), // pending, in_progress, completed
  priority: text("priority").default("normal"), // low, normal, urgent
  notes: text("notes"),
  address: text("address"),
  scheduledDate: timestamp("scheduled_date"),
  deadlineAt: timestamp("deadline_at"),
  completionPhotos: jsonb("completion_photos").$type<string[]>(),
  completedAt: timestamp("completed_at"),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  jobType: text("job_type").default("checkout"), // checkout, intra_stay
  customFeeOverride: numeric("custom_fee_override", { precision: 10, scale: 2 }),
  customCleanerPayOverride: numeric("custom_cleaner_pay_override", { precision: 10, scale: 2 }),
  adminNotes: text("admin_notes"),
  assignedOrgId: integer("assigned_org_id").references(() => organizations.id, { onDelete: "set null" }),
  cancelledAt: timestamp("cancelled_at"),
  cancelledByUserId: integer("cancelled_by_user_id").references(() => users.id, { onDelete: "set null" }),
  manuallyScheduled: boolean("manually_scheduled").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});
export const insertCleaningJobSchema = createInsertSchema(cleaningJobs).omit({ id: true });

// === CLEANING JOB PHOTOS (categorized by area) ===
export const cleaningJobPhotos = pgTable("cleaning_job_photos", {
  id: serial("id").primaryKey(),
  cleaningJobId: integer("cleaning_job_id").references(() => cleaningJobs.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  category: text("category").notNull(), // kitchen, living_room, bathrooms, floor, fridge
  url: text("url").notNull(),
  uploadedByUserId: integer("uploaded_by_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCleaningJobPhotoSchema = createInsertSchema(cleaningJobPhotos).omit({ id: true, createdAt: true });

// === CLEANING TASKS (legacy - kept for backward compat, will be removed) ===
export const cleaningTasks = pgTable("cleaning_tasks", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  title: text("title").notNull(),
  status: text("status").notNull().default("pending"),
  assignedTo: text("assigned_to"),
  notes: text("notes"),
  priority: text("priority").default("normal"),
  dueDate: timestamp("due_date"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCleaningTaskSchema = createInsertSchema(cleaningTasks).omit({ id: true });

// === GENERAL TASKS === (RecurrenceRule type + photos in ./general-task-recurrence.ts)
export const generalTasks = pgTable("general_tasks", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  title: text("title").notNull(),
  description: text("description"),
  assignedTo: integer("assigned_to").references(() => users.id, { onDelete: "set null" }),
  scheduledAt: timestamp("scheduled_at"),
  address: text("address"), // nullable — use when not linked to an apartment
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }),
  taskType: text("task_type").notNull().default("other"), // checkin | checkout | unit_setup | inspection | laundry | other
  status: text("status").notNull().default("pending"), // pending | in_progress | completed
  billable: boolean("billable").notNull().default(false),
  billingAmount: numeric("billing_amount", { precision: 10, scale: 2 }),
  billedToUserId: integer("billed_to_user_id").references(() => users.id, { onDelete: "set null" }),
  cleanerPayoutAmount: numeric("cleaner_payout_amount", { precision: 10, scale: 2 }),
  secondAssigneeId: integer("second_assignee_id").references(() => users.id, { onDelete: "set null" }),
  cleaningJobId: integer("cleaning_job_id").references(() => cleaningJobs.id, { onDelete: "set null" }),
  recurrenceRule: jsonb("recurrence_rule").$type<import("./general-task-recurrence").RecurrenceRule | null>(), // migration 0046
  recurrenceEndDate: timestamp("recurrence_end_date"),
  recurrenceParentId: integer("recurrence_parent_id"), // FK to general_tasks(id) in 0046, not declared here (self-ref)
  nextOccurrenceAt: timestamp("next_occurrence_at"),
  isRecurringTemplate: boolean("is_recurring_template").notNull().default(false),
  completedAt: timestamp("completed_at"),
  completionNotes: text("completion_notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGeneralTaskSchema = createInsertSchema(generalTasks).omit({ id: true, createdAt: true, updatedAt: true });
export type GeneralTask = typeof generalTasks.$inferSelect;
export type InsertGeneralTask = z.infer<typeof insertGeneralTaskSchema>;

// === LAUNDRY JOBS ===
export type LaundryItem = { name: string; qty: number };

export const laundryJobs = pgTable("laundry_jobs", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  cleaningJobId: integer("cleaning_job_id").references(() => cleaningJobs.id, { onDelete: "set null" }),
  assignedTo: integer("assigned_to").references(() => users.id, { onDelete: "set null" }),
  status: text("status").notNull().default("pending"), // pending | in_progress | completed
  takenItems: jsonb("taken_items").$type<LaundryItem[]>().default([]),
  returnedItems: jsonb("returned_items").$type<LaundryItem[]>().default([]),
  mismatch: boolean("mismatch").notNull().default(false),
  mismatchDetails: jsonb("mismatch_details").$type<{ name: string; taken: number; returned: number }[]>(),
  notes: text("notes"),
  scheduledAt: timestamp("scheduled_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertLaundryJobSchema = createInsertSchema(laundryJobs).omit({ id: true, createdAt: true, updatedAt: true });
export type LaundryJob = typeof laundryJobs.$inferSelect;
export type InsertLaundryJob = z.infer<typeof insertLaundryJobSchema>;

export const laundryOrgSettings = pgTable("laundry_org_settings", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull().unique(),
  showTakenItemsToLaundry: boolean("show_taken_items_to_laundry").notNull().default(false),
  assignmentMode: text("assignment_mode").notNull().default("manual"), // manual | specific_user | round_robin
  specificUserId: integer("specific_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertLaundryOrgSettingsSchema = createInsertSchema(laundryOrgSettings).omit({ id: true, createdAt: true, updatedAt: true });
export type LaundryOrgSettings = typeof laundryOrgSettings.$inferSelect;

export const userRoles = pgTable("user_roles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  role: text("role").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});
export type UserRole = typeof userRoles.$inferSelect;

// === SUBMISSIONS ===
export const submissions = pgTable("submissions", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  guestName: text("guest_name").notNull(),
  guestPhone: text("guest_phone").notNull(),
  guestEmail: text("guest_email"),
  timestamp: timestamp("timestamp").defaultNow(),
  fobFrontUrl: text("fob_front_url"),
  fobBackUrl: text("fob_back_url"),
  checklistResults: jsonb("checklist_results"),
  photoResults: jsonb("photo_results"),
});

export const insertSubmissionSchema = createInsertSchema(submissions).omit({ id: true });


// === COMMISSION SETTINGS ===
export const commissionSettings = pgTable("commission_settings", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id),
  checkInFee: numeric("check_in_fee", { precision: 10, scale: 2 }).notNull().default("50.00"),
  cleaningFee: numeric("cleaning_fee", { precision: 10, scale: 2 }).notNull().default("110.00"),
  cleanerPay: numeric("cleaner_pay", { precision: 10, scale: 2 }).notNull().default("75.00"),
  cleaningFeeStudio: numeric("cleaning_fee_studio", { precision: 10, scale: 2 }).default("90.00"),
  cleanerPayStudio: numeric("cleaner_pay_studio", { precision: 10, scale: 2 }).default("60.00"),
  cleaningFee2br: numeric("cleaning_fee_2br", { precision: 10, scale: 2 }).default("140.00"),
  cleanerPay2br: numeric("cleaner_pay_2br", { precision: 10, scale: 2 }).default("95.00"),
  cleaningFee3br: numeric("cleaning_fee_3br", { precision: 10, scale: 2 }).default("170.00"),
  cleanerPay3br: numeric("cleaner_pay_3br", { precision: 10, scale: 2 }).default("115.00"),
  cleaningFee4br: numeric("cleaning_fee_4br", { precision: 10, scale: 2 }).default("200.00"),
  cleanerPay4br: numeric("cleaner_pay_4br", { precision: 10, scale: 2 }).default("140.00"),
  cleaningFee5br: numeric("cleaning_fee_5br", { precision: 10, scale: 2 }).default("240.00"),
  cleanerPay5br: numeric("cleaner_pay_5br", { precision: 10, scale: 2 }).default("165.00"),
  intraStayFee: numeric("intra_stay_fee", { precision: 10, scale: 2 }).default("80.00"),
  intraStayCleanerPay: numeric("intra_stay_cleaner_pay", { precision: 10, scale: 2 }).default("60.00"),
  partnerCommissionPct: numeric("partner_commission_pct", { precision: 5, scale: 2 }).default("14.00"),
  partnerName: text("partner_name").default("Partner"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCommissionSettingsSchema = createInsertSchema(commissionSettings).omit({ id: true, updatedAt: true });

// === COMMISSION TIERS (dynamic per-bedroom-count pricing) ===
export const commissionTiers = pgTable("commission_tiers", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id),
  bedrooms: integer("bedrooms").notNull(), // 0=Studio, 1=1BR, 2=2BR, ...
  cleaningFee: numeric("cleaning_fee", { precision: 10, scale: 2 }).notNull(),
  cleanerPay: numeric("cleaner_pay", { precision: 10, scale: 2 }).notNull(),
}, (t) => ({
  uniq: uniqueIndex("commission_tiers_org_bedrooms_idx").on(t.organizationId, t.bedrooms),
}));

export const insertCommissionTierSchema = createInsertSchema(commissionTiers).omit({ id: true });
export type CommissionTier = typeof commissionTiers.$inferSelect;
export type InsertCommissionTier = z.infer<typeof insertCommissionTierSchema>;

// === FINANCIAL RECORDS ===
export const financialRecords = pgTable("financial_records", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "set null" }),
  type: text("type").notNull(), // check_in, cleaning, intra_stay_cleaning, maintenance, miscellaneous, unit_setup, deposit_collected, deposit_returned, admin_withdrawal, booking_commission, credit, org_expense
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  cleaningJobId: integer("cleaning_job_id").references(() => cleaningJobs.id, { onDelete: "set null" }),
  maintenanceJobId: integer("maintenance_job_id").references(() => maintenanceJobs.id, { onDelete: "set null" }),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  cleanerUserId: integer("cleaner_user_id").references(() => users.id, { onDelete: "set null" }),
  totalAmount: numeric("total_amount", { precision: 10, scale: 2 }).notNull(),
  adminAmount: numeric("admin_amount", { precision: 10, scale: 2 }).notNull(),
  cleanerAmount: numeric("cleaner_amount", { precision: 10, scale: 2 }).default("0.00"),
  partnerAmount: numeric("partner_amount", { precision: 10, scale: 2 }).default("0.00"),
  description: text("description"),
  paymentMethod: text("payment_method").default("cash"), // cash, bank_transfer, zelle, airbnb_payout, venmo, other
  status: text("status").notNull().default("pending"), // pending, paid, cancelled
  receiptUrls: jsonb("receipt_urls").$type<string[]>(),
  paidAt: timestamp("paid_at"),
  recordDate: timestamp("record_date"),
  sourceContractId: integer("source_contract_id"), // FK to org_contracts(id) — enforced at DB level (migration 0020)
  recurringChargeId: integer("recurring_charge_id"), // FK to recurring_charges(id) — added migration 0026
  generalTaskId: integer("general_task_id"), // FK to general_tasks(id) — added migration 0027
  unitCostId: integer("unit_cost_id"), // FK to unit_costs(id) — added migration 0041
  reversedAt: timestamp("reversed_at"),
  reversalReason: text("reversal_reason"),
  originalPaidAt: timestamp("original_paid_at"),
  reversedByUserId: integer("reversed_by_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertFinancialRecordSchema = createInsertSchema(financialRecords).omit({ id: true, createdAt: true });

// === RECURRING CHARGES ===
export const recurringCharges = pgTable("recurring_charges", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  description: text("description").notNull(),
  category: text("category").notNull().default("miscellaneous"), // miscellaneous | storage | other | salary | utility_water | utility_electric | utility_gas
  // 0047 — direction of the charge. 'pm' = billed to the property manager (current behavior, positive admin income).
  // 'org_expense' = absorbed by the org, emits a negative-adminAmount financial record that reduces admin commission.
  billingTarget: text("billing_target").notNull().default("pm"), // pm | org_expense
  // 0047 — when billingTarget='org_expense' and this is a payment to a person (e.g. a contractor salary),
  // the user being paid. Used to also record a contractorPayment (1099) alongside the P&L expense.
  payeeUserId: integer("payee_user_id").references(() => users.id, { onDelete: "set null" }),
  frequency: text("frequency").notNull().default("monthly"),     // monthly | weekly | biweekly
  dayOfMonth: integer("day_of_month"),  // 1–31, used when frequency = 'monthly'
  dayOfWeek: integer("day_of_week"),    // legacy single-day (0–6); new charges use daysOfWeek
  daysOfWeek: jsonb("days_of_week").$type<number[] | null>(), endsAt: timestamp("ends_at"), // 0046 — multi-day weekly/biweekly + hard stop
  // 0052 — optional start date. If set in the past at creation, the missed periods (up to endsAt, or
  // now if there is no end) are backfilled immediately; if set in the future, the first run anchors to it.
  startsAt: timestamp("starts_at"),
  nextRunAt: timestamp("next_run_at").notNull(),
  lastRunAt: timestamp("last_run_at"),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertRecurringChargeSchema = createInsertSchema(recurringCharges).omit({ id: true, createdAt: true, updatedAt: true });
export type RecurringCharge = typeof recurringCharges.$inferSelect;
export type InsertRecurringCharge = z.infer<typeof insertRecurringChargeSchema>;

// === SECURITY DEPOSITS ===
export const securityDeposits = pgTable("security_deposits", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  guestName: text("guest_name"),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  status: text("status").notNull().default("collected"), // collected, held, returned, partially_returned, forfeited
  returnedAmount: numeric("returned_amount", { precision: 10, scale: 2 }).default("0.00"),
  paymentMethod: text("payment_method").default("cash"), // cash, bank_transfer, zelle, venmo, other
  notes: text("notes"),
  collectedAt: timestamp("collected_at").defaultNow(),
  returnedAt: timestamp("returned_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSecurityDepositSchema = createInsertSchema(securityDeposits).omit({ id: true, createdAt: true });

// === CASH TRANSACTIONS (Caja Ledger) ===
export const cashTransactions = pgTable("cash_transactions", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  type: text("type").notNull(), // income, expense, withdrawal, deposit_in, deposit_out
  category: text("category").notNull(), // check_in_fee, cleaning_fee, maintenance, deposit_collected, deposit_returned, admin_withdrawal, cleaner_payment, owner_payment, miscellaneous
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  paymentMethod: text("payment_method").default("cash"), // cash, bank_transfer, zelle, venmo, other
  financialRecordId: integer("financial_record_id").references(() => financialRecords.id, { onDelete: "set null" }),
  securityDepositId: integer("security_deposit_id").references(() => securityDeposits.id, { onDelete: "set null" }),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  description: text("description"),
  performedBy: integer("performed_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCashTransactionSchema = createInsertSchema(cashTransactions).omit({ id: true, createdAt: true });

// === BILLING PLANS (platform billing for organizations) ===
export const billingPlans = pgTable("billing_plans", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  model: text("model").notNull().default("flat"), // flat, per_apartment, per_usage, custom
  currency: text("currency").notNull().default("USD"),
  flatAmount: numeric("flat_amount", { precision: 10, scale: 2 }).default("0.00"),
  perApartmentAmount: numeric("per_apartment_amount", { precision: 10, scale: 2 }).default("0.00"),
  perUsageAmount: numeric("per_usage_amount", { precision: 10, scale: 2 }).default("0.00"),
  customAmount: numeric("custom_amount", { precision: 10, scale: 2 }).default("0.00"),
  billingCycle: text("billing_cycle").notNull().default("monthly"), // monthly, quarterly, yearly
  isActive: boolean("is_active").default(true),
  modulesEnabled: jsonb("modules_enabled").$type<string[]>().default([
    "buildings_units", "reservations", "cleaning_jobs", "submissions_checkout",
    "content_guides", "finance_core", "reports_basic"
  ]),
  limitsJson: jsonb("limits_json").$type<{
    max_units?: number;
    max_users?: number;
    included_checkins_month?: number;
    included_checkouts_month?: number;
    max_push_devices?: number;
  }>().default({}),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertBillingPlanSchema = createInsertSchema(billingPlans).omit({ id: true, createdAt: true, updatedAt: true });

// === BILLING INVOICES ===
export const billingInvoices = pgTable("billing_invoices", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  model: text("model").notNull(), // snapshot of billing model at time of invoice
  apartmentCount: integer("apartment_count").default(0),
  usageCount: integer("usage_count").default(0),
  subtotal: numeric("subtotal", { precision: 10, scale: 2 }).notNull().default("0.00"),
  adjustments: numeric("adjustments", { precision: 10, scale: 2 }).default("0.00"),
  total: numeric("total", { precision: 10, scale: 2 }).notNull().default("0.00"),
  status: text("status").notNull().default("draft"), // draft, issued, paid, overdue, void
  notes: text("notes"),
  issuedAt: timestamp("issued_at"),
  paidAt: timestamp("paid_at"),
  dueDate: timestamp("due_date"),
  stripeInvoiceId: text("stripe_invoice_id"),
  stripeInvoiceUrl: text("stripe_invoice_url"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBillingInvoiceSchema = createInsertSchema(billingInvoices).omit({ id: true, createdAt: true });

// === PUSH SUBSCRIPTIONS ===
export const pushSubscriptions = pgTable("push_subscriptions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }),
  endpoint: text("endpoint").notNull(),
  p256dhKey: text("p256dh_key").notNull(),
  authKey: text("auth_key").notNull(),
  deviceLabel: text("device_label"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({ id: true, createdAt: true });

// === NOTIFICATIONS (in-app notification center) ===
export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  type: text("type").notNull(), // job_assigned, job_urgent, checkout_submitted, reservation_changed, invoice_issued, etc.
  title: text("title").notNull(),
  body: text("body"),
  data: jsonb("data").$type<Record<string, any>>(),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({ id: true, createdAt: true });

// === CHECKIN SUBMISSIONS ===
export const checkinSubmissions = pgTable("checkin_submissions", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  guestName: text("guest_name").notNull(),
  guestPhone: text("guest_phone"),
  guestEmail: text("guest_email"),
  rulesAccepted: boolean("rules_accepted").default(false),
  notes: text("notes"),
  photoUrls: jsonb("photo_urls").$type<string[]>(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCheckinSubmissionSchema = createInsertSchema(checkinSubmissions).omit({ id: true, createdAt: true });

// === SUPPORT TICKETS ===
export const tickets = pgTable("tickets", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  createdByUserId: integer("created_by_user_id").references(() => users.id, { onDelete: "set null" }),
  guestName: text("guest_name"),
  guestEmail: text("guest_email"),
  category: text("category").notNull().default("general"), // access, maintenance, cleaning_issue, billing, general
  priority: text("priority").notNull().default("medium"), // low, medium, high
  status: text("status").notNull().default("open"), // open, in_progress, closed
  subject: text("subject").notNull(),
  assignedToUserId: integer("assigned_to_user_id").references(() => users.id, { onDelete: "set null" }),
  cleaningJobId: integer("cleaning_job_id").references(() => cleaningJobs.id, { onDelete: "set null" }),
  laundryJobId: integer("laundry_job_id").references(() => laundryJobs.id, { onDelete: "set null" }),
  closedAt: timestamp("closed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertTicketSchema = createInsertSchema(tickets).omit({ id: true, createdAt: true });

// === TICKET MESSAGES ===
export const ticketMessages = pgTable("ticket_messages", {
  id: serial("id").primaryKey(),
  ticketId: integer("ticket_id").references(() => tickets.id, { onDelete: "cascade" }).notNull(),
  senderUserId: integer("sender_user_id").references(() => users.id, { onDelete: "set null" }),
  senderName: text("sender_name"),
  body: text("body").notNull(),
  attachmentUrls: jsonb("attachment_urls").$type<string[]>(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertTicketMessageSchema = createInsertSchema(ticketMessages).omit({ id: true, createdAt: true });

// === MAINTENANCE JOBS (linked to tickets) ===
export const maintenanceJobs = pgTable("maintenance_jobs", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  ticketId: integer("ticket_id").references(() => tickets.id, { onDelete: "set null" }),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "set null" }).notNull(),
  assignedToUserId: integer("assigned_to_user_id").references(() => users.id, { onDelete: "set null" }),
  title: text("title").notNull(),
  description: text("description"),
  priority: text("priority").notNull().default("medium"), // low, medium, high, urgent
  status: text("status").notNull().default("pending"), // pending, pending_approval, approved, assigned, in_progress, completed, cancelled
  isBillable: boolean("is_billable").default(false),
  costAmount: numeric("cost_amount", { precision: 10, scale: 2 }).default("0.00"),
  costDescription: text("cost_description"),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  approvedByUserId: integer("approved_by_user_id").references(() => users.id, { onDelete: "set null" }),
  approvedAt: timestamp("approved_at"),
  rejectionReason: text("rejection_reason"),
  scheduledDate: timestamp("scheduled_date"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMaintenanceJobSchema = createInsertSchema(maintenanceJobs).omit({ id: true, createdAt: true });

export const maintenanceJobPhotos = pgTable("maintenance_job_photos", {
  id: serial("id").primaryKey(),
  maintenanceJobId: integer("maintenance_job_id").references(() => maintenanceJobs.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  url: text("url").notNull(),
  caption: text("caption"),
  phase: text("phase").notNull().default("after"),
  uploadedByUserId: integer("uploaded_by_user_id").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMaintenanceJobPhotoSchema = createInsertSchema(maintenanceJobPhotos).omit({ id: true, createdAt: true });

// === ORGANIZATION BILLING PROFILES (admin company info for invoices) ===
export const orgBillingProfiles = pgTable("org_billing_profiles", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull().unique(),
  companyName: text("company_name").notNull(),
  taxId: text("tax_id"),
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: text("state"),
  postalCode: text("postal_code"),
  country: text("country").default("US"),
  billingEmail: text("billing_email"),
  billingPhone: text("billing_phone"),
  logoUrl: text("logo_url"),
  paymentMethods: jsonb("payment_methods").$type<Array<{
    type: string;
    label: string;
    details: string;
    isDefault?: boolean;
  }>>(),
  bankName: text("bank_name"),
  bankAccountName: text("bank_account_name"),
  bankAccountNumber: text("bank_account_number"),
  bankRoutingNumber: text("bank_routing_number"),
  bankSwiftCode: text("bank_swift_code"),
  zelleName: text("zelle_name"),
  zelleEmail: text("zelle_email"),
  zellePhone: text("zelle_phone"),
  venmoHandle: text("venmo_handle"),
  paypalEmail: text("paypal_email"),
  defaultTaxRate: numeric("default_tax_rate", { precision: 5, scale: 2 }),
  defaultTaxLabel: text("default_tax_label"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertOrgBillingProfileSchema = createInsertSchema(orgBillingProfiles).omit({ id: true, updatedAt: true });
export type OrgBillingProfile = typeof orgBillingProfiles.$inferSelect;
export type InsertOrgBillingProfile = z.infer<typeof insertOrgBillingProfileSchema>;

// === OWNER BILLING PROFILES (company info for invoices) ===
export const ownerBillingProfiles = pgTable("owner_billing_profiles", {
  id: serial("id").primaryKey(),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  companyName: text("company_name").notNull(),
  taxId: text("tax_id"),
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: text("state"),
  postalCode: text("postal_code"),
  country: text("country").default("US"),
  billingEmail: text("billing_email"),
  billingPhone: text("billing_phone"),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Auto-invoice override (null = use org default)
  autoInvoicePeriod: text("auto_invoice_period"),
  autoInvoiceDayOfMonth: integer("auto_invoice_day_of_month"),
  autoInvoiceDayOfWeek: integer("auto_invoice_day_of_week"),
  autoInvoiceLastRunAt: timestamp("auto_invoice_last_run_at"),
});

export const insertOwnerBillingProfileSchema = createInsertSchema(ownerBillingProfiles).omit({ id: true, updatedAt: true });

// === CLEANER BILLING PROFILES ===
export const cleanerBillingProfiles = pgTable("cleaner_billing_profiles", {
  id: serial("id").primaryKey(),
  cleanerUserId: integer("cleaner_user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  companyName: text("company_name").notNull(),
  taxId: text("tax_id"),
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: text("state"),
  postalCode: text("postal_code"),
  country: text("country").default("US"),
  billingEmail: text("billing_email"),
  billingPhone: text("billing_phone"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertCleanerBillingProfileSchema = createInsertSchema(cleanerBillingProfiles).omit({ id: true, updatedAt: true });

// === OWNER INVOICES (generated from reports for owners) ===
export const ownerInvoices = pgTable("owner_invoices", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  invoiceNumber: text("invoice_number").notNull(),
  status: text("status").notNull().default("draft"),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  lineItems: jsonb("line_items").$type<Array<{
    description: string;
    category: string;
    amount: string;
    date?: string;
    quantity?: number; financialRecordId?: number; unitCostId?: number; // 2026-09-24: source ids, used to never bill a charge twice
  }>>().notNull(),
  subtotal: numeric("subtotal", { precision: 10, scale: 2 }).notNull().default("0.00"),
  total: numeric("total", { precision: 10, scale: 2 }).notNull().default("0.00"),
  cashCollected: numeric("cash_collected", { precision: 10, scale: 2 }).default("0.00"),
  settlements: numeric("settlements", { precision: 10, scale: 2 }).default("0.00"),
  netOwed: numeric("net_owed", { precision: 10, scale: 2 }).default("0.00"),
  paidAmount: numeric("paid_amount", { precision: 10, scale: 2 }).notNull().default("0.00"),
  companySnapshot: jsonb("company_snapshot").$type<{
    companyName: string;
    taxId?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    billingEmail?: string;
    billingPhone?: string;
  }>(),
  adminCompanySnapshot: jsonb("admin_company_snapshot").$type<{
    companyName: string;
    taxId?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    billingEmail?: string;
    billingPhone?: string;
  }>(),
  billedToOrgId: integer("billed_to_org_id").references(() => organizations.id, { onDelete: "set null" }),
  platformFeeAmount: numeric("platform_fee_amount", { precision: 10, scale: 2 }).notNull().default("0.00"),
  publicToken: text("public_token").unique(),
  stripePaymentSessionId: text("stripe_payment_session_id"),
  paidAt: timestamp("paid_at", { withTimezone: true }),
  notes: text("notes"),
  issuedAt: timestamp("issued_at"),
  approvedAt: timestamp("approved_at"),
  sentAt: timestamp("sent_at"),
  sentTo: text("sent_to"),
  refundedAt: timestamp("refunded_at", { withTimezone: true }),
  refundedBy: integer("refunded_by").references(() => users.id, { onDelete: "set null" }),
  refundAmount: numeric("refund_amount", { precision: 10, scale: 2 }),
  refundReason: text("refund_reason"),
  revertedAt: timestamp("reverted_at", { withTimezone: true }),
  revertedBy: integer("reverted_by").references(() => users.id, { onDelete: "set null" }),
  revertReason: text("revert_reason"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertOwnerInvoiceSchema = createInsertSchema(ownerInvoices).omit({ id: true, createdAt: true });

// === INVOICE PAYMENTS (partial payment ledger for owner invoices) ===
export const invoicePayments = pgTable("invoice_payments", {
  id: serial("id").primaryKey(),
  invoiceId: integer("invoice_id").references(() => ownerInvoices.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  paymentMethod: text("payment_method").default("bank_transfer"),
  notes: text("notes"),
  recordedBy: integer("recorded_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertInvoicePaymentSchema = createInsertSchema(invoicePayments).omit({ id: true, createdAt: true });

// === CROSS-ORG CONTRACTS + GRANTS ===

/** INTER-org contracts. NOT the same as `contracts` (guest e-signature templates). */
export const orgContracts = pgTable("org_contracts", {
  id: serial("id").primaryKey(),
  providerOrgId: integer("provider_org_id").references(() => organizations.id, { onDelete: "restrict" }).notNull(),
  consumerOrgId: integer("consumer_org_id").references(() => organizations.id, { onDelete: "restrict" }).notNull(),
  contractType: text("contract_type").notNull(),
  status: text("status").notNull().default("proposed"),
  title: text("title").notNull(),
  terms: jsonb("terms").$type<Record<string, unknown>>().notNull().default({}),
  startsAt: timestamp("starts_at", { withTimezone: true }),
  endsAt: timestamp("ends_at", { withTimezone: true }).notNull(),
  proposedByUserId: integer("proposed_by_user_id").references(() => users.id),
  acceptedByUserId: integer("accepted_by_user_id").references(() => users.id),
  acceptedAt: timestamp("accepted_at", { withTimezone: true }),
  terminatedAt: timestamp("terminated_at", { withTimezone: true }),
  terminatedReason: text("terminated_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

export const orgDataGrants = pgTable("org_data_grants", {
  id: serial("id").primaryKey(),
  contractId: integer("contract_id").references(() => orgContracts.id, { onDelete: "cascade" }).notNull(),
  grantedToOrgId: integer("granted_to_org_id").references(() => organizations.id, { onDelete: "restrict" }).notNull(),
  resourceType: text("resource_type").notNull().default("apartment"),
  resourceId: integer("resource_id").notNull(),
  permissions: jsonb("permissions").$type<string[]>().notNull().default([]),
  grantedAt: timestamp("granted_at", { withTimezone: true }).notNull().defaultNow(),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  revokedAt: timestamp("revoked_at", { withTimezone: true }),
  createdByUserId: integer("created_by_user_id").references(() => users.id),
});

export type OrgContract = typeof orgContracts.$inferSelect;
export type InsertOrgContract = typeof orgContracts.$inferInsert;
export type OrgDataGrant = typeof orgDataGrants.$inferSelect;
export type InsertOrgDataGrant = typeof orgDataGrants.$inferInsert;

// === ORGANIZATION FEATURE FLAGS ===
export const FEATURE_KEYS = [
  "buildings_units",
  "reservations",
  "integrations_ical",
  "cleaning_jobs",
  "submissions_checkout",
  "content_guides",
  "ai_concierge",
  "finance_core",
  "reports_basic",
  "billing_invoices",
  "notifications_push",
  "support_tickets",
  "booking_engine",
  "channel_manager",
  "guest_payments",
  "upsells_addons",
  "advanced_reports",
  "smart_locks",
  "custom_fields",
  "commissions",
  "affiliates",
  "marketplace",
  "contractor_invoices",
  "tax_compliance",
  "data_retention",
  "maintenance_ops",
  "lumi_ai",
  "guest_crm",
  "automation_engine",
  "inventory",
  "cleaning_inventory",
  "cash_box",
  "unit_costs",
  "daily_reports",
  "forecast",
  "integrations_admin",
  "qa_vision",
  "team_management",
  "team_management_edit",
] as const;

export type FeatureKey = typeof FEATURE_KEYS[number];

export const TRIAL_ENABLED_FEATURES: readonly FeatureKey[] = [
  "buildings_units",
  "reservations",
  "integrations_ical",
  "cleaning_jobs",
  "submissions_checkout",
  "content_guides",
  "finance_core",
  "reports_basic",
  "notifications_push",
  "support_tickets",
  "guest_payments",
  "maintenance_ops",
] as const;

export const FEATURE_LABELS: Record<string, string> = {
  buildings_units: "Buildings & Units",
  reservations: "Reservations",
  integrations_ical: "iCal Integrations",
  cleaning_jobs: "Cleaning Jobs",
  submissions_checkout: "Submissions & Checkout",
  content_guides: "Content & Guides",
  ai_concierge: "AI Concierge",
  finance_core: "Finance Core",
  reports_basic: "Reports & Analytics",
  billing_invoices: "Billing & Invoices",
  notifications_push: "Push Notifications",
  support_tickets: "Support Tickets",
  booking_engine: "Booking Engine",
  channel_manager: "Channel Manager",
  guest_payments: "Guest Payments",
  upsells_addons: "Upsells & Add-ons",
  advanced_reports: "Advanced Reports",
  smart_locks: "Smart Locks",
  custom_fields: "Custom Fields",
  commissions: "Commissions",
  affiliates: "Affiliates",
  marketplace: "Marketplace",
  contractor_invoices: "Contractor Invoices",
  tax_compliance: "Tax Compliance & 1099",
  data_retention: "Data Retention Policy",
  maintenance_ops: "Maintenance Operations",
  lumi_ai: "Lumi AI Assistant",
  guest_crm: "Guest CRM",
  automation_engine: "Automation Engine",
  inventory: "Inventory Management",
  cleaning_inventory: "Cleaning Inventory",
  cash_box: "Caja / Cash Box",
  unit_costs: "Unit Cost Tracking",
  daily_reports: "Daily Reports",
  forecast: "Forecast & Analytics",
  integrations_admin: "Integrations",
  qa_vision: "QA Vision (AI Scanning)",
  team_management: "Team Management (View)",
  team_management_edit: "Team Management (Edit)",
};

export const orgFeatures = pgTable("org_features", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  featureKey: text("feature_key").notNull(),
  enabled: boolean("enabled").notNull().default(true),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertOrgFeatureSchema = createInsertSchema(orgFeatures).omit({ id: true, updatedAt: true });
export type OrgFeature = typeof orgFeatures.$inferSelect;
export type InsertOrgFeature = z.infer<typeof insertOrgFeatureSchema>;

// === BOOKING ENGINE ===
export const bookingPages = pgTable("booking_pages", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  slug: text("slug").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  heroImage: text("hero_image"),
  isActive: boolean("is_active").notNull().default(true),
  primaryColor: text("primary_color").default("#FF5A5F"),
  minStay: integer("min_stay").default(1),
  maxStay: integer("max_stay").default(365),
  advanceBookingDays: integer("advance_booking_days").default(365),
  termsAndConditions: text("terms_and_conditions"),
  cancellationPolicy: text("cancellation_policy"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBookingPageSchema = createInsertSchema(bookingPages).omit({ id: true, createdAt: true });
export type BookingPage = typeof bookingPages.$inferSelect;
export type InsertBookingPage = z.infer<typeof insertBookingPageSchema>;

export const ratePlans = pgTable("rate_plans", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  bookingPageId: integer("booking_page_id").references(() => bookingPages.id, { onDelete: "set null" }),
  name: text("name").notNull(),
  baseNightlyRate: numeric("base_nightly_rate", { precision: 10, scale: 2 }).notNull(),
  cleaningFee: numeric("cleaning_fee", { precision: 10, scale: 2 }).default("0"),
  weekendRate: numeric("weekend_rate", { precision: 10, scale: 2 }),
  weeklyDiscount: integer("weekly_discount").default(0),
  monthlyDiscount: integer("monthly_discount").default(0),
  currency: text("currency").notNull().default("USD"),
  taxRate: numeric("tax_rate", { precision: 5, scale: 2 }).default("0"),
  taxName: text("tax_name").default("Tax"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertRatePlanSchema = createInsertSchema(ratePlans).omit({ id: true, createdAt: true });
export type RatePlan = typeof ratePlans.$inferSelect;
export type InsertRatePlan = z.infer<typeof insertRatePlanSchema>;

export const bookingHolds = pgTable("booking_holds", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id).notNull(),
  apartmentId: integer("apartment_id").references(() => apartments.id).notNull(),
  ratePlanId: integer("rate_plan_id").references(() => ratePlans.id).notNull(),
  checkIn: text("check_in").notNull(),
  checkOut: text("check_out").notNull(),
  guestName: text("guest_name"),
  guestEmail: text("guest_email"),
  totalAmount: numeric("total_amount", { precision: 10, scale: 2 }).notNull(),
  status: text("status").notNull().default("held"),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBookingHoldSchema = createInsertSchema(bookingHolds).omit({ id: true, createdAt: true });
export type BookingHold = typeof bookingHolds.$inferSelect;
export type InsertBookingHold = z.infer<typeof insertBookingHoldSchema>;

export const bookingCheckoutSessions = pgTable("booking_checkout_sessions", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id).notNull(),
  bookingHoldId: integer("booking_hold_id").references(() => bookingHolds.id).notNull(),
  guestName: text("guest_name").notNull(),
  guestEmail: text("guest_email").notNull(),
  guestPhone: text("guest_phone"),
  totalAmount: numeric("total_amount", { precision: 10, scale: 2 }).notNull(),
  paymentStatus: text("payment_status").notNull().default("pending"),
  paymentProvider: text("payment_provider"),
  paymentReference: text("payment_reference"),
  status: text("status").notNull().default("pending"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBookingCheckoutSessionSchema = createInsertSchema(bookingCheckoutSessions).omit({ id: true, createdAt: true, completedAt: true });
export type BookingCheckoutSession = typeof bookingCheckoutSessions.$inferSelect;
export type InsertBookingCheckoutSession = z.infer<typeof insertBookingCheckoutSessionSchema>;

// === RELATIONS ===
export const organizationsRelations = relations(organizations, ({ many }) => ({
  users: many(users),
  buildings: many(buildings),
  apartments: many(apartments),
  billingPlans: many(billingPlans),
  billingInvoices: many(billingInvoices),
}));

export const usersRelations = relations(users, ({ one, many }) => ({
  organization: one(organizations, { fields: [users.organizationId], references: [organizations.id] }),
  apartments: many(apartments),
  assignedJobs: many(cleaningJobs),
  reservationsCreated: many(reservations),
}));

export const buildingsRelations = relations(buildings, ({ one, many }) => ({
  organization: one(organizations, { fields: [buildings.organizationId], references: [organizations.id] }),
  apartments: many(apartments),
}));

export const apartmentsRelations = relations(apartments, ({ one, many }) => ({
  organization: one(organizations, { fields: [apartments.organizationId], references: [organizations.id] }),
  building: one(buildings, { fields: [apartments.buildingId], references: [buildings.id] }),
  owner: one(users, { fields: [apartments.ownerUserId], references: [users.id] }),
  sections: many(sections),
  checklistItems: many(checklistItems),
  photoRequirements: many(photoRequirements),
  submissions: many(submissions),
  cleaningJobs: many(cleaningJobs),
  reservations: many(reservations),
}));

export const reservationsRelations = relations(reservations, ({ one }) => ({
  apartment: one(apartments, { fields: [reservations.apartmentId], references: [apartments.id] }),
  creator: one(users, { fields: [reservations.createdBy], references: [users.id] }),
}));

export const cleaningJobsRelations = relations(cleaningJobs, ({ one, many }) => ({
  apartment: one(apartments, { fields: [cleaningJobs.apartmentId], references: [apartments.id] }),
  submission: one(submissions, { fields: [cleaningJobs.submissionId], references: [submissions.id] }),
  assignee: one(users, { fields: [cleaningJobs.assignedTo], references: [users.id] }),
  reservation: one(reservations, { fields: [cleaningJobs.reservationId], references: [reservations.id], relationName: "cleaningJobReservation" }),
  nextReservation: one(reservations, { fields: [cleaningJobs.nextReservationId], references: [reservations.id], relationName: "cleaningJobNextReservation" }),
  photos: many(cleaningJobPhotos),
}));

export const cleaningJobPhotosRelations = relations(cleaningJobPhotos, ({ one }) => ({
  cleaningJob: one(cleaningJobs, { fields: [cleaningJobPhotos.cleaningJobId], references: [cleaningJobs.id] }),
  organization: one(organizations, { fields: [cleaningJobPhotos.organizationId], references: [organizations.id] }),
  uploadedBy: one(users, { fields: [cleaningJobPhotos.uploadedByUserId], references: [users.id] }),
}));

export const cleaningTasksRelations = relations(cleaningTasks, ({ one }) => ({
  apartment: one(apartments, { fields: [cleaningTasks.apartmentId], references: [apartments.id] }),
}));

export const submissionsRelations = relations(submissions, ({ one }) => ({
  apartment: one(apartments, { fields: [submissions.apartmentId], references: [apartments.id] }),
}));

export const financialRecordsRelations = relations(financialRecords, ({ one }) => ({
  apartment: one(apartments, { fields: [financialRecords.apartmentId], references: [apartments.id] }),
  reservation: one(reservations, { fields: [financialRecords.reservationId], references: [reservations.id] }),
  cleaningJob: one(cleaningJobs, { fields: [financialRecords.cleaningJobId], references: [cleaningJobs.id] }),
  owner: one(users, { fields: [financialRecords.ownerUserId], references: [users.id], relationName: "financialRecordOwner" }),
  cleaner: one(users, { fields: [financialRecords.cleanerUserId], references: [users.id], relationName: "financialRecordCleaner" }),
  recurringCharge: one(recurringCharges, { fields: [financialRecords.recurringChargeId], references: [recurringCharges.id] }),
}));

export const recurringChargesRelations = relations(recurringCharges, ({ one, many }) => ({
  organization: one(organizations, { fields: [recurringCharges.organizationId], references: [organizations.id] }),
  owner: one(users, { fields: [recurringCharges.ownerUserId], references: [users.id] }),
  apartment: one(apartments, { fields: [recurringCharges.apartmentId], references: [apartments.id] }),
  financialRecords: many(financialRecords),
}));

export const billingPlansRelations = relations(billingPlans, ({ one }) => ({
  organization: one(organizations, { fields: [billingPlans.organizationId], references: [organizations.id] }),
}));

export const billingInvoicesRelations = relations(billingInvoices, ({ one }) => ({
  organization: one(organizations, { fields: [billingInvoices.organizationId], references: [organizations.id] }),
}));

export const pushSubscriptionsRelations = relations(pushSubscriptions, ({ one }) => ({
  user: one(users, { fields: [pushSubscriptions.userId], references: [users.id] }),
  organization: one(organizations, { fields: [pushSubscriptions.organizationId], references: [organizations.id] }),
}));

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, { fields: [notifications.userId], references: [users.id] }),
  organization: one(organizations, { fields: [notifications.organizationId], references: [organizations.id] }),
}));

export const checkinSubmissionsRelations = relations(checkinSubmissions, ({ one }) => ({
  apartment: one(apartments, { fields: [checkinSubmissions.apartmentId], references: [apartments.id] }),
  reservation: one(reservations, { fields: [checkinSubmissions.reservationId], references: [reservations.id] }),
}));

export const ticketsRelations = relations(tickets, ({ one, many }) => ({
  organization: one(organizations, { fields: [tickets.organizationId], references: [organizations.id] }),
  apartment: one(apartments, { fields: [tickets.apartmentId], references: [apartments.id] }),
  reservation: one(reservations, { fields: [tickets.reservationId], references: [reservations.id] }),
  creator: one(users, { fields: [tickets.createdByUserId], references: [users.id], relationName: "ticketCreator" }),
  assignee: one(users, { fields: [tickets.assignedToUserId], references: [users.id], relationName: "ticketAssignee" }),
  cleaningJob: one(cleaningJobs, { fields: [tickets.cleaningJobId], references: [cleaningJobs.id] }),
  messages: many(ticketMessages),
}));

export const laundryJobsRelations = relations(laundryJobs, ({ one }) => ({
  organization: one(organizations, { fields: [laundryJobs.organizationId], references: [organizations.id] }),
  cleaningJob: one(cleaningJobs, { fields: [laundryJobs.cleaningJobId], references: [cleaningJobs.id] }),
  assignee: one(users, { fields: [laundryJobs.assignedTo], references: [users.id] }),
}));

export const ticketMessagesRelations = relations(ticketMessages, ({ one }) => ({
  ticket: one(tickets, { fields: [ticketMessages.ticketId], references: [tickets.id] }),
  sender: one(users, { fields: [ticketMessages.senderUserId], references: [users.id] }),
}));

export const maintenanceJobsRelations = relations(maintenanceJobs, ({ one, many }) => ({
  organization: one(organizations, { fields: [maintenanceJobs.organizationId], references: [organizations.id] }),
  ticket: one(tickets, { fields: [maintenanceJobs.ticketId], references: [tickets.id] }),
  apartment: one(apartments, { fields: [maintenanceJobs.apartmentId], references: [apartments.id] }),
  assignee: one(users, { fields: [maintenanceJobs.assignedToUserId], references: [users.id] }),
  photos: many(maintenanceJobPhotos),
}));

export const maintenanceJobPhotosRelations = relations(maintenanceJobPhotos, ({ one }) => ({
  maintenanceJob: one(maintenanceJobs, { fields: [maintenanceJobPhotos.maintenanceJobId], references: [maintenanceJobs.id] }),
  organization: one(organizations, { fields: [maintenanceJobPhotos.organizationId], references: [organizations.id] }),
  uploadedBy: one(users, { fields: [maintenanceJobPhotos.uploadedByUserId], references: [users.id] }),
}));

export const securityDepositsRelations = relations(securityDeposits, ({ one }) => ({
  organization: one(organizations, { fields: [securityDeposits.organizationId], references: [organizations.id] }),
  reservation: one(reservations, { fields: [securityDeposits.reservationId], references: [reservations.id] }),
  apartment: one(apartments, { fields: [securityDeposits.apartmentId], references: [apartments.id] }),
  owner: one(users, { fields: [securityDeposits.ownerUserId], references: [users.id] }),
}));

export const cashTransactionsRelations = relations(cashTransactions, ({ one }) => ({
  organization: one(organizations, { fields: [cashTransactions.organizationId], references: [organizations.id] }),
  financialRecord: one(financialRecords, { fields: [cashTransactions.financialRecordId], references: [financialRecords.id] }),
  securityDeposit: one(securityDeposits, { fields: [cashTransactions.securityDepositId], references: [securityDeposits.id] }),
  apartment: one(apartments, { fields: [cashTransactions.apartmentId], references: [apartments.id] }),
  reservation: one(reservations, { fields: [cashTransactions.reservationId], references: [reservations.id] }),
  owner: one(users, { fields: [cashTransactions.ownerUserId], references: [users.id] }),
  performer: one(users, { fields: [cashTransactions.performedBy], references: [users.id] }),
}));

export const ownerBillingProfilesRelations = relations(ownerBillingProfiles, ({ one }) => ({
  owner: one(users, { fields: [ownerBillingProfiles.ownerUserId], references: [users.id] }),
  organization: one(organizations, { fields: [ownerBillingProfiles.organizationId], references: [organizations.id] }),
}));

export const cleanerBillingProfilesRelations = relations(cleanerBillingProfiles, ({ one }) => ({
  cleaner: one(users, { fields: [cleanerBillingProfiles.cleanerUserId], references: [users.id] }),
  organization: one(organizations, { fields: [cleanerBillingProfiles.organizationId], references: [organizations.id] }),
}));

export const ownerInvoicesRelations = relations(ownerInvoices, ({ one, many }) => ({
  organization: one(organizations, { fields: [ownerInvoices.organizationId], references: [organizations.id] }),
  owner: one(users, { fields: [ownerInvoices.ownerUserId], references: [users.id] }),
  payments: many(invoicePayments),
}));

export const invoicePaymentsRelations = relations(invoicePayments, ({ one }) => ({
  invoice: one(ownerInvoices, { fields: [invoicePayments.invoiceId], references: [ownerInvoices.id] }),
  organization: one(organizations, { fields: [invoicePayments.organizationId], references: [organizations.id] }),
  recorder: one(users, { fields: [invoicePayments.recordedBy], references: [users.id] }),
}));

export const bookingPagesRelations = relations(bookingPages, ({ one, many }) => ({
  organization: one(organizations, { fields: [bookingPages.organizationId], references: [organizations.id] }),
  ratePlans: many(ratePlans),
}));

export const ratePlansRelations = relations(ratePlans, ({ one }) => ({
  organization: one(organizations, { fields: [ratePlans.organizationId], references: [organizations.id] }),
  apartment: one(apartments, { fields: [ratePlans.apartmentId], references: [apartments.id] }),
  bookingPage: one(bookingPages, { fields: [ratePlans.bookingPageId], references: [bookingPages.id] }),
}));

export const bookingHoldsRelations = relations(bookingHolds, ({ one }) => ({
  organization: one(organizations, { fields: [bookingHolds.organizationId], references: [organizations.id] }),
  apartment: one(apartments, { fields: [bookingHolds.apartmentId], references: [apartments.id] }),
  ratePlan: one(ratePlans, { fields: [bookingHolds.ratePlanId], references: [ratePlans.id] }),
}));

export const bookingCheckoutSessionsRelations = relations(bookingCheckoutSessions, ({ one }) => ({
  organization: one(organizations, { fields: [bookingCheckoutSessions.organizationId], references: [organizations.id] }),
  bookingHold: one(bookingHolds, { fields: [bookingCheckoutSessions.bookingHoldId], references: [bookingHolds.id] }),
}));

// === PHASE 2: CHANNEL MANAGER ===
export const channelConnections = pgTable("channel_connections", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  provider: text("provider").notNull(),
  status: text("status").notNull().default("active"),
  icalImportUrl: text("ical_import_url"),
  icalExportUrl: text("ical_export_url"),
  externalListingId: text("external_listing_id"),
  syncEnabled: boolean("sync_enabled").notNull().default(true),
  lastSyncAt: timestamp("last_sync_at"),
  lastSyncStatus: text("last_sync_status"),
  syncError: text("sync_error"),
  lastSyncReservationCount: integer("last_sync_reservation_count"),
  settings: jsonb("settings"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertChannelConnectionSchema = createInsertSchema(channelConnections).omit({ id: true, createdAt: true });
export type ChannelConnection = typeof channelConnections.$inferSelect;
export type InsertChannelConnection = z.infer<typeof insertChannelConnectionSchema>;

// === PHASE 2: GUEST PAYMENTS ===
export const guestPayments = pgTable("guest_payments", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "cascade" }).notNull(),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  currency: text("currency").notNull().default("USD"),
  method: text("method").notNull().default("cash"),
  status: text("status").notNull().default("pending"),
  dueDate: timestamp("due_date"),
  paidAt: timestamp("paid_at"),
  externalRef: text("external_ref"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGuestPaymentSchema = createInsertSchema(guestPayments).omit({ id: true, createdAt: true });
export type GuestPayment = typeof guestPayments.$inferSelect;
export type InsertGuestPayment = z.infer<typeof insertGuestPaymentSchema>;

// === PHASE 2: UPSELLS & ADD-ONS ===
export const upsellItems = pgTable("upsell_items", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  name: text("name").notNull(),
  description: text("description"),
  price: numeric("price", { precision: 10, scale: 2 }).notNull(),
  currency: text("currency").notNull().default("USD"),
  category: text("category").notNull().default("general"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUpsellItemSchema = createInsertSchema(upsellItems).omit({ id: true, createdAt: true });
export type UpsellItem = typeof upsellItems.$inferSelect;
export type InsertUpsellItem = z.infer<typeof insertUpsellItemSchema>;

export const upsellOrders = pgTable("upsell_orders", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "cascade" }).notNull(),
  upsellItemId: integer("upsell_item_id").references(() => upsellItems.id, { onDelete: "cascade" }).notNull(),
  quantity: integer("quantity").notNull().default(1),
  unitPrice: numeric("unit_price", { precision: 10, scale: 2 }).notNull(),
  totalPrice: numeric("total_price", { precision: 10, scale: 2 }).notNull(),
  status: text("status").notNull().default("pending"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUpsellOrderSchema = createInsertSchema(upsellOrders).omit({ id: true, createdAt: true });
export type UpsellOrder = typeof upsellOrders.$inferSelect;
export type InsertUpsellOrder = z.infer<typeof insertUpsellOrderSchema>;

// === PHASE 2: SMART LOCKS ===
export const smartLocks = pgTable("smart_locks", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  provider: text("provider").notNull().default("generic"),
  deviceId: text("device_id").notNull(),
  name: text("name").notNull(),
  status: text("status").notNull().default("active"),
  batteryLevel: integer("battery_level"),
  lastSeenAt: timestamp("last_seen_at"),
  settings: jsonb("settings"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSmartLockSchema = createInsertSchema(smartLocks).omit({ id: true, createdAt: true });
export type SmartLock = typeof smartLocks.$inferSelect;
export type InsertSmartLock = z.infer<typeof insertSmartLockSchema>;

export const accessCodes = pgTable("access_codes", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  smartLockId: integer("smart_lock_id").references(() => smartLocks.id, { onDelete: "cascade" }).notNull(),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  code: text("code").notNull(),
  label: text("label"),
  validFrom: timestamp("valid_from").notNull(),
  validTo: timestamp("valid_to").notNull(),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAccessCodeSchema = createInsertSchema(accessCodes).omit({ id: true, createdAt: true });
export type AccessCode = typeof accessCodes.$inferSelect;
export type InsertAccessCode = z.infer<typeof insertAccessCodeSchema>;

// === PHASE 4: COMMISSION RULES ===
export const commissionRules = pgTable("commission_rules", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  name: text("name").notNull(),
  scope: text("scope").notNull().default("org"),
  scopeEntityId: integer("scope_entity_id"),
  sourceFilter: text("source_filter"),
  beneficiaryType: text("beneficiary_type").notNull(),
  beneficiaryId: integer("beneficiary_id"),
  commissionPct: numeric("commission_pct", { precision: 5, scale: 2 }).notNull().default("0"),
  commissionFixed: numeric("commission_fixed", { precision: 10, scale: 2 }).default("0"),
  priority: integer("priority").default(0),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCommissionRuleSchema = createInsertSchema(commissionRules).omit({ id: true, createdAt: true });
export type CommissionRule = typeof commissionRules.$inferSelect;
export type InsertCommissionRule = z.infer<typeof insertCommissionRuleSchema>;

// === PHASE 4: COMMISSION ALLOCATIONS ===
export const commissionAllocations = pgTable("commission_allocations", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "cascade" }).notNull(),
  commissionRuleId: integer("commission_rule_id").references(() => commissionRules.id, { onDelete: "set null" }),
  beneficiaryType: text("beneficiary_type").notNull(),
  beneficiaryId: integer("beneficiary_id"),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  percentage: numeric("percentage", { precision: 5, scale: 2 }),
  status: text("status").notNull().default("pending"),
  paidAt: timestamp("paid_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCommissionAllocationSchema = createInsertSchema(commissionAllocations).omit({ id: true, createdAt: true });
export type CommissionAllocation = typeof commissionAllocations.$inferSelect;
export type InsertCommissionAllocation = z.infer<typeof insertCommissionAllocationSchema>;

// === PHASE 4: AFFILIATE PROFILES ===
export const affiliateProfiles = pgTable("affiliate_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  referralCode: text("referral_code").notNull().unique(),
  commissionPct: numeric("commission_pct", { precision: 5, scale: 2 }).notNull().default("10"),
  status: text("status").notNull().default("active"),
  totalEarnings: numeric("total_earnings", { precision: 10, scale: 2 }).default("0"),
  totalReferrals: integer("total_referrals").default(0),
  payoutMethod: text("payout_method"),
  payoutDetails: text("payout_details"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAffiliateProfileSchema = createInsertSchema(affiliateProfiles).omit({ id: true, createdAt: true });
export type AffiliateProfile = typeof affiliateProfiles.$inferSelect;
export type InsertAffiliateProfile = z.infer<typeof insertAffiliateProfileSchema>;

// === PHASE 2: CUSTOM FIELDS ===
export const customFields = pgTable("custom_fields", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  entityType: text("entity_type").notNull(),
  fieldKey: text("field_key").notNull(),
  label: text("label").notNull(),
  fieldType: text("field_type").notNull().default("text"),
  options: jsonb("options"),
  isRequired: boolean("is_required").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCustomFieldSchema = createInsertSchema(customFields).omit({ id: true, createdAt: true });
export type CustomField = typeof customFields.$inferSelect;
export type InsertCustomField = z.infer<typeof insertCustomFieldSchema>;

export const customFieldValues = pgTable("custom_field_values", {
  id: serial("id").primaryKey(),
  customFieldId: integer("custom_field_id").references(() => customFields.id, { onDelete: "cascade" }).notNull(),
  entityId: integer("entity_id").notNull(),
  valueText: text("value_text"),
  valueNumber: numeric("value_number", { precision: 15, scale: 4 }),
  valueBoolean: boolean("value_boolean"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCustomFieldValueSchema = createInsertSchema(customFieldValues).omit({ id: true, createdAt: true });
export type CustomFieldValue = typeof customFieldValues.$inferSelect;
export type InsertCustomFieldValue = z.infer<typeof insertCustomFieldValueSchema>;

// === USER PROFILES (onboarding, W-9, bank info, ID) ===
export const userProfiles = pgTable("user_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }).notNull().unique(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }),
  onboardingCompleted: boolean("onboarding_completed").default(false),
  requirePersonalInfo: boolean("require_personal_info").default(true),
  requireIdUpload: boolean("require_id_upload").default(true),
  requireW9: boolean("require_w9").default(false),
  requireBankInfo: boolean("require_bank_info").default(false),
  personalInfoCompleted: boolean("personal_info_completed").default(false),
  idUploadCompleted: boolean("id_upload_completed").default(false),
  w9Completed: boolean("w9_completed").default(false),
  bankInfoCompleted: boolean("bank_info_completed").default(false),
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  dateOfBirth: text("date_of_birth"),
  emergencyContactName: text("emergency_contact_name"),
  emergencyContactPhone: text("emergency_contact_phone"),
  idFrontUrl: text("id_front_url"),
  idBackUrl: text("id_back_url"),
  idType: text("id_type"),
  w9LegalName: text("w9_legal_name"),
  w9BusinessName: text("w9_business_name"),
  w9TaxClassification: text("w9_tax_classification"),
  w9Ssn: text("w9_ssn"),
  w9Ein: text("w9_ein"),
  w9SsnEncrypted: text("w9_ssn_encrypted"),
  w9EinEncrypted: text("w9_ein_encrypted"),
  w9SsnLast4: text("w9_ssn_last4"),
  w9EinLast4: text("w9_ein_last4"),
  w9Address: text("w9_address"),
  w9City: text("w9_city"),
  w9State: text("w9_state"),
  w9ZipCode: text("w9_zip_code"),
  w9SignatureDate: text("w9_signature_date"),
  w9ExemptPayeeCode: text("w9_exempt_payee_code"),
  w9DocumentUrl: text("w9_document_url"),
  entityType: text("entity_type"),
  is1099Required: boolean("is_1099_required"),
  exemptionReason: text("exemption_reason"),
  eDeliveryConsent: boolean("e_delivery_consent").default(false),
  tinMatchStatus: text("tin_match_status"),
  tinRetentionYears: integer("tin_retention_years").default(7),
  tinDeleteAt: timestamp("tin_delete_at"),
  tinRedacted: boolean("tin_redacted").default(false),
  bankName: text("bank_name"),
  bankAccountNumber: text("bank_account_number"),
  bankRoutingNumber: text("bank_routing_number"),
  bankAccountNumberEncrypted: text("bank_account_number_encrypted"),
  bankRoutingNumberEncrypted: text("bank_routing_number_encrypted"),
  bankAccountNumberLast4: text("bank_account_number_last4"),
  bankRoutingNumberLast4: text("bank_routing_number_last4"),
  bankAccountType: text("bank_account_type"),
  zelleEmail: text("zelle_email"),
  zellePhone: text("zelle_phone"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({ id: true, createdAt: true, updatedAt: true });
export type UserProfile = typeof userProfiles.$inferSelect;
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;

// === CONTRACTOR INVOICES ===
export const contractorInvoices = pgTable("contractor_invoices", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  contractorUserId: integer("contractor_user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  invoiceNumber: text("invoice_number").notNull(),
  weekStartDate: timestamp("week_start_date").notNull(),
  weekEndDate: timestamp("week_end_date").notNull(),
  totalAmount: numeric("total_amount", { precision: 10, scale: 2 }).notNull().default("0.00"),
  status: text("status").notNull().default("draft"),
  notes: text("notes"),
  approvedBy: integer("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  paidAt: timestamp("paid_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertContractorInvoiceSchema = createInsertSchema(contractorInvoices).omit({ id: true, createdAt: true });
export type ContractorInvoice = typeof contractorInvoices.$inferSelect;
export type InsertContractorInvoice = z.infer<typeof insertContractorInvoiceSchema>;

// === CONTRACTOR INVOICE LINE ITEMS ===
export const contractorInvoiceItems = pgTable("contractor_invoice_items", {
  id: serial("id").primaryKey(),
  invoiceId: integer("invoice_id").references(() => contractorInvoices.id, { onDelete: "cascade" }).notNull(),
  financialRecordId: integer("financial_record_id").references(() => financialRecords.id, { onDelete: "set null" }),
  description: text("description").notNull(),
  amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
  jobType: text("job_type"),
  jobDate: timestamp("job_date"),
});

export const insertContractorInvoiceItemSchema = createInsertSchema(contractorInvoiceItems).omit({ id: true });
export type ContractorInvoiceItem = typeof contractorInvoiceItems.$inferSelect;
export type InsertContractorInvoiceItem = z.infer<typeof insertContractorInvoiceItemSchema>;

// === AUDIT LOGS (security compliance) ===
export const auditLogs = pgTable("audit_logs", {
  id: serial("id").primaryKey(),
  actorUserId: integer("actor_user_id").references(() => users.id, { onDelete: "set null" }),
  targetUserId: integer("target_user_id").references(() => users.id, { onDelete: "set null" }),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "set null" }),
  action: text("action").notNull(),
  resource: text("resource").notNull(),
  resourceId: integer("resource_id"),
  reason: text("reason"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({ id: true, createdAt: true });
export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;

// === PAYER TAX PROFILE (company info for 1099 filing) ===
export const payerTaxProfiles = pgTable("payer_tax_profiles", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull().unique(),
  legalBusinessName: text("legal_business_name"),
  ein: text("ein"),
  einEncrypted: text("ein_encrypted"),
  einLast4: text("ein_last4"),
  address: text("address"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  contactName: text("contact_name"),
  contactPhone: text("contact_phone"),
  contactEmail: text("contact_email"),
  transmitterControlCode: text("transmitter_control_code"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPayerTaxProfileSchema = createInsertSchema(payerTaxProfiles).omit({ id: true, createdAt: true, updatedAt: true });
export type PayerTaxProfile = typeof payerTaxProfiles.$inferSelect;
export type InsertPayerTaxProfile = z.infer<typeof insertPayerTaxProfileSchema>;

// === CONTRACTOR PAYMENT LEDGER (immutable payment records) ===
export const contractorPayments = pgTable("contractor_payments", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  contractorUserId: integer("contractor_user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  datePaid: timestamp("date_paid").notNull(),
  amount: numeric("amount", { precision: 12, scale: 2 }).notNull(),
  method: text("method").notNull(),
  referenceId: text("reference_id"),
  reservationId: integer("reservation_id"),
  cleaningJobId: integer("cleaning_job_id"),
  maintenanceJobId: integer("maintenance_job_id"),
  invoiceId: integer("invoice_id").references(() => contractorInvoices.id, { onDelete: "set null" }),
  description: text("description"),
  category: text("category").default("service"),
  status: text("status").default("paid").notNull(),
  voidedAt: timestamp("voided_at"),
  voidedBy: integer("voided_by"),
  voidReason: text("void_reason"),
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: integer("created_by").references(() => users.id, { onDelete: "set null" }),
});

export const insertContractorPaymentSchema = createInsertSchema(contractorPayments).omit({ id: true, createdAt: true });
export type ContractorPayment = typeof contractorPayments.$inferSelect;
export type InsertContractorPayment = z.infer<typeof insertContractorPaymentSchema>;

// === 1099 YEAR SUMMARY ===
export const form1099Summaries = pgTable("form_1099_summaries", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  contractorUserId: integer("contractor_user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  taxYear: integer("tax_year").notNull(),
  totalNonemployeeComp: numeric("total_nonemployee_comp", { precision: 12, scale: 2 }).default("0"),
  totalReimbursements: numeric("total_reimbursements", { precision: 12, scale: 2 }).default("0"),
  filingStatus: text("filing_status").default("draft"),
  w9SnapshotData: jsonb("w9_snapshot_data"),
  payerSnapshotData: jsonb("payer_snapshot_data"),
  generatedPdfUrl: text("generated_pdf_url"),
  deliveredAt: timestamp("delivered_at"),
  deliveryMethod: text("delivery_method"),
  efileSubmittedAt: timestamp("efile_submitted_at"),
  efileAcceptedAt: timestamp("efile_accepted_at"),
  efileRejectedAt: timestamp("efile_rejected_at"),
  correctedFromId: integer("corrected_from_id"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertForm1099SummarySchema = createInsertSchema(form1099Summaries).omit({ id: true, createdAt: true, updatedAt: true });
export type Form1099Summary = typeof form1099Summaries.$inferSelect;
export type InsertForm1099Summary = z.infer<typeof insertForm1099SummarySchema>;

// === EXPLICIT TYPES ===
export type Organization = typeof organizations.$inferSelect;
export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;
export type User = typeof users.$inferSelect;
export type Building = typeof buildings.$inferSelect;
export type Apartment = typeof apartments.$inferSelect;
export type Section = typeof sections.$inferSelect;
export type ChecklistItem = typeof checklistItems.$inferSelect;
export type PhotoRequirement = typeof photoRequirements.$inferSelect;
export type Reservation = typeof reservations.$inferSelect;
export type CleaningJob = typeof cleaningJobs.$inferSelect;
export type CleaningTask = typeof cleaningTasks.$inferSelect;
export type Submission = typeof submissions.$inferSelect;

export type InsertUser = z.infer<typeof insertUserSchema>;
export type InsertBuilding = z.infer<typeof insertBuildingSchema>;
export type InsertApartment = z.infer<typeof insertApartmentSchema>;
export type InsertSection = z.infer<typeof insertSectionSchema>;
export type InsertChecklistItem = z.infer<typeof insertChecklistItemSchema>;
export type InsertPhotoRequirement = z.infer<typeof insertPhotoRequirementSchema>;
export type InsertReservation = z.infer<typeof insertReservationSchema>;
export type InsertCleaningJob = z.infer<typeof insertCleaningJobSchema>;
export type InsertCleaningTask = z.infer<typeof insertCleaningTaskSchema>;
export type InsertSubmission = z.infer<typeof insertSubmissionSchema>;
export type CommissionSettings = typeof commissionSettings.$inferSelect;
export type InsertCommissionSettings = z.infer<typeof insertCommissionSettingsSchema>;
export type FinancialRecord = typeof financialRecords.$inferSelect;
export type InsertFinancialRecord = z.infer<typeof insertFinancialRecordSchema>;
export type BillingPlan = typeof billingPlans.$inferSelect;
export type InsertBillingPlan = z.infer<typeof insertBillingPlanSchema>;
export type BillingInvoice = typeof billingInvoices.$inferSelect;
export type InsertBillingInvoice = z.infer<typeof insertBillingInvoiceSchema>;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type CheckinSubmission = typeof checkinSubmissions.$inferSelect;
export type InsertCheckinSubmission = z.infer<typeof insertCheckinSubmissionSchema>;
export type Ticket = typeof tickets.$inferSelect;
export type InsertTicket = z.infer<typeof insertTicketSchema>;
export type TicketMessage = typeof ticketMessages.$inferSelect;
export type InsertTicketMessage = z.infer<typeof insertTicketMessageSchema>;
export type MaintenanceJob = typeof maintenanceJobs.$inferSelect;
export type InsertMaintenanceJob = z.infer<typeof insertMaintenanceJobSchema>;
export type MaintenanceJobPhoto = typeof maintenanceJobPhotos.$inferSelect;
export type InsertMaintenanceJobPhoto = z.infer<typeof insertMaintenanceJobPhotoSchema>;
export type CleaningJobPhoto = typeof cleaningJobPhotos.$inferSelect;
export type InsertCleaningJobPhoto = z.infer<typeof insertCleaningJobPhotoSchema>;
export type SecurityDeposit = typeof securityDeposits.$inferSelect;
export type InsertSecurityDeposit = z.infer<typeof insertSecurityDepositSchema>;
export type CashTransaction = typeof cashTransactions.$inferSelect;
export type InsertCashTransaction = z.infer<typeof insertCashTransactionSchema>;
export type OwnerBillingProfile = typeof ownerBillingProfiles.$inferSelect;
export type InsertOwnerBillingProfile = z.infer<typeof insertOwnerBillingProfileSchema>;
export type CleanerBillingProfile = typeof cleanerBillingProfiles.$inferSelect;
export type InsertCleanerBillingProfile = z.infer<typeof insertCleanerBillingProfileSchema>;
export type OwnerInvoice = typeof ownerInvoices.$inferSelect;
export type InsertOwnerInvoice = z.infer<typeof insertOwnerInvoiceSchema>;
export type InvoicePayment = typeof invoicePayments.$inferSelect;
export type InsertInvoicePayment = z.infer<typeof insertInvoicePaymentSchema>;

export const hostLeads = pgTable("host_leads", {
  id: serial("id").primaryKey(),
  fullName: text("full_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone"),
  company: text("company"),
  propertyCount: text("property_count"),
  location: text("location"),
  message: text("message"),
  status: text("status").default("new"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertHostLeadSchema = createInsertSchema(hostLeads).omit({ id: true, status: true, createdAt: true });
export type HostLead = typeof hostLeads.$inferSelect;
export type InsertHostLead = z.infer<typeof insertHostLeadSchema>;

// === CRM CONVERSATIONS ===
export const crmConversations = pgTable("crm_conversations", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id),
  platform: text("platform").notNull().default("internal"),
  guestName: text("guest_name").notNull(),
  guestEmail: text("guest_email"),
  guestPhone: text("guest_phone"),
  guestAddress: text("guest_address"),
  guestCompany: text("guest_company"),
  guestLanguage: text("guest_language").default("en"),
  reservationId: integer("reservation_id"),
  apartmentId: integer("apartment_id"),
  guestToken: text("guest_token"),
  status: text("status").notNull().default("open"),
  pipelineStage: text("pipeline_stage").notNull().default("new"),
  tags: text("tags").array().default([]),
  notes: text("notes"),
  assignedTo: integer("assigned_to"),
  priority: text("priority").notNull().default("normal"),
  source: text("source"),
  totalSpend: text("total_spend"),
  lastStayDate: timestamp("last_stay_date"),
  stayCount: integer("stay_count").default(0),
  unreadCount: integer("unread_count").default(0).notNull(),
  lastMessageAt: timestamp("last_message_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCrmConversationSchema = createInsertSchema(crmConversations).omit({ id: true, unreadCount: true, lastMessageAt: true, createdAt: true });
export type CrmConversation = typeof crmConversations.$inferSelect;
export type InsertCrmConversation = z.infer<typeof insertCrmConversationSchema>;

// === CRM MESSAGES ===
export const crmMessages = pgTable("crm_messages", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id").references(() => crmConversations.id).notNull(),
  direction: text("direction").notNull().default("inbound"),
  channel: text("channel").notNull().default("internal"),
  body: text("body").notNull(),
  senderName: text("sender_name"),
  sentAt: timestamp("sent_at").defaultNow(),
  status: text("status").notNull().default("delivered"),
});

export const insertCrmMessageSchema = createInsertSchema(crmMessages).omit({ id: true, sentAt: true });
export type CrmMessage = typeof crmMessages.$inferSelect;
export type InsertCrmMessage = z.infer<typeof insertCrmMessageSchema>;

// === CRM ACTIVITIES (Timeline Events) ===
export const crmActivities = pgTable("crm_activities", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id").references(() => crmConversations.id).notNull(),
  type: text("type").notNull(),
  description: text("description").notNull(),
  metadata: jsonb("metadata").default({}),
  performedBy: text("performed_by"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCrmActivitySchema = createInsertSchema(crmActivities).omit({ id: true, createdAt: true });
export type CrmActivity = typeof crmActivities.$inferSelect;
export type InsertCrmActivity = z.infer<typeof insertCrmActivitySchema>;

// === CRM TASKS (Follow-ups) ===
export const crmTasks = pgTable("crm_tasks", {
  id: serial("id").primaryKey(),
  conversationId: integer("conversation_id").references(() => crmConversations.id).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id),
  title: text("title").notNull(),
  description: text("description"),
  dueDate: timestamp("due_date"),
  assignedTo: integer("assigned_to"),
  status: text("status").notNull().default("pending"),
  priority: text("priority").notNull().default("normal"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCrmTaskSchema = createInsertSchema(crmTasks).omit({ id: true, completedAt: true, createdAt: true });
export type CrmTask = typeof crmTasks.$inferSelect;
export type InsertCrmTask = z.infer<typeof insertCrmTaskSchema>;

// === CRM TEMPLATES (Quick Replies) ===
export const crmTemplates = pgTable("crm_templates", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id),
  name: text("name").notNull(),
  category: text("category").notNull().default("general"),
  body: text("body").notNull(),
  channel: text("channel").default("internal"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCrmTemplateSchema = createInsertSchema(crmTemplates).omit({ id: true, createdAt: true });
export type CrmTemplate = typeof crmTemplates.$inferSelect;
export type InsertCrmTemplate = z.infer<typeof insertCrmTemplateSchema>;

// === AUTOMATION RULES ===
export const automationRules = pgTable("automation_rules", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id),
  name: text("name").notNull(),
  description: text("description"),
  triggerType: text("trigger_type").notNull(),
  conditions: jsonb("conditions").default({}),
  actions: jsonb("actions").default([]),
  enabled: boolean("enabled").notNull().default(true),
  lastRunAt: timestamp("last_run_at"),
  runCount: integer("run_count").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAutomationRuleSchema = createInsertSchema(automationRules).omit({ id: true, lastRunAt: true, runCount: true, createdAt: true });
export type AutomationRule = typeof automationRules.$inferSelect;
export type InsertAutomationRule = z.infer<typeof insertAutomationRuleSchema>;

// === AUTOMATION RUNS ===
export const automationRuns = pgTable("automation_runs", {
  id: serial("id").primaryKey(),
  ruleId: integer("rule_id").references(() => automationRules.id).notNull(),
  status: text("status").notNull().default("success"),
  triggeredBy: text("triggered_by"),
  payload: jsonb("payload").default({}),
  result: jsonb("result").default({}),
  ranAt: timestamp("ran_at").defaultNow(),
});

export const insertAutomationRunSchema = createInsertSchema(automationRuns).omit({ id: true, ranAt: true });
export type AutomationRun = typeof automationRuns.$inferSelect;
export type InsertAutomationRun = z.infer<typeof insertAutomationRunSchema>;

export const unitCosts = pgTable("unit_costs", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull(),
  apartmentId: integer("apartment_id").references(() => apartments.id),
  category: text("category").notNull(),
  description: text("description"),
  amount: text("amount").notNull().default("0"),
  date: timestamp("date"),
  recurring: boolean("recurring").default(false),
  recurringFrequency: text("recurring_frequency"),
  vendor: text("vendor"),
  receiptUrl: text("receipt_url"),
  chargedToPm: boolean("charged_to_pm").default(false),
  // 0047 — true = the org absorbs this expense (reduces admin commission via a negative-adminAmount
  // org_expense financial record). Distinct from chargedToPm=false cleaner-reimbursement costs, which
  // are invisible to the P&L. Mutually exclusive with chargedToPm in the UI.
  isOrgExpense: boolean("is_org_expense").notNull().default(false),
  paidToCleaner: boolean("paid_to_cleaner").default(false),
  marginPct: text("margin_pct"),
  cleaningJobId: integer("cleaning_job_id").references(() => cleaningJobs.id),
  cleanerPayoutAmount: numeric("cleaner_payout_amount", { precision: 10, scale: 2 }),
  status: text("status").notNull().default("pending"), // pending, invoiced
  createdBy: integer("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUnitCostSchema = createInsertSchema(unitCosts).omit({ id: true, createdAt: true });
export type UnitCost = typeof unitCosts.$inferSelect;
export type InsertUnitCost = z.infer<typeof insertUnitCostSchema>;

export const supplyCatalog = pgTable("supply_catalog", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull(),
  name: text("name").notNull(),
  category: text("category").notNull().default("cleaning"),
  unitOfMeasure: text("unit_of_measure").notNull().default("unit"),
  costPerUnit: text("cost_per_unit").notNull().default("0"),
  qtyPerJobStudio: text("qty_per_job_studio").notNull().default("1"),
  qtyPerJob1br: text("qty_per_job_1br").notNull().default("1"),
  qtyPerJob2br: text("qty_per_job_2br").notNull().default("2"),
  qtyPerJob3br: text("qty_per_job_3br").notNull().default("3"),
  qtyPerJob1bath: text("qty_per_job_1bath").notNull().default("0"),
  qtyPerJob2bath: text("qty_per_job_2bath").notNull().default("0"),
  qtyPerJob3bath: text("qty_per_job_3bath").notNull().default("0"),
  reorderPoint: text("reorder_point").notNull().default("10"),
  active: boolean("active").notNull().default(true),
  typicalOwner: text("typical_owner").notNull().default("org"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSupplyCatalogSchema = createInsertSchema(supplyCatalog).omit({ id: true, createdAt: true });
export type SupplyCatalogItem = typeof supplyCatalog.$inferSelect;
export type InsertSupplyCatalogItem = z.infer<typeof insertSupplyCatalogSchema>;

export const supplyInventory = pgTable("supply_inventory", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull(),
  supplyCatalogId: integer("supply_catalog_id").notNull(),
  ownerType: text("owner_type").notNull().default("org"),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  currentStock: text("current_stock").notNull().default("0"),
  reorderPoint: text("reorder_point").notNull().default("10"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSupplyInventorySchema = createInsertSchema(supplyInventory).omit({ id: true, updatedAt: true });
export type SupplyInventoryItem = typeof supplyInventory.$inferSelect;
export type InsertSupplyInventoryItem = z.infer<typeof insertSupplyInventorySchema>;

export const supplyMovements = pgTable("supply_movements", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull(),
  supplyCatalogId: integer("supply_catalog_id").notNull(),
  ownerType: text("owner_type").notNull().default("org"),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  type: text("type").notNull(),
  quantity: text("quantity").notNull(),
  reference: text("reference"),
  cleaningJobId: integer("cleaning_job_id"),
  notes: text("notes"),
  performedBy: integer("performed_by"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSupplyMovementSchema = createInsertSchema(supplyMovements).omit({ id: true, createdAt: true });
export type SupplyMovement = typeof supplyMovements.$inferSelect;
export type InsertSupplyMovement = z.infer<typeof insertSupplyMovementSchema>;

export const supplyPurchases = pgTable("supply_purchases", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").notNull(),
  supplyCatalogId: integer("supply_catalog_id").notNull(),
  quantity: text("quantity").notNull(),
  totalCost: text("total_cost").notNull().default("0"),
  supplier: text("supplier"),
  purchaseDate: timestamp("purchase_date").defaultNow(),
  notes: text("notes"),
  performedBy: integer("performed_by"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSupplyPurchaseSchema = createInsertSchema(supplyPurchases).omit({ id: true, createdAt: true });
export type SupplyPurchase = typeof supplyPurchases.$inferSelect;
export type InsertSupplyPurchase = z.infer<typeof insertSupplyPurchaseSchema>;

// === DAILY REPORT SETTINGS ===
export const dailyReportSettings = pgTable("daily_report_settings", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id).notNull(),
  name: text("name").notNull().default("Daily Report"),
  enabled: boolean("enabled").notNull().default(true),
  sendTime: text("send_time").notNull().default("18:00"),
  timezone: text("timezone").notNull().default("America/New_York"),
  recipients: jsonb("recipients").notNull().default([]),
  includeRevenue: boolean("include_revenue").notNull().default(true),
  includeOccupancy: boolean("include_occupancy").notNull().default(true),
  includeCleaningStatus: boolean("include_cleaning_status").notNull().default(true),
  includeMaintenanceTickets: boolean("include_maintenance_tickets").notNull().default(true),
  includeReservations: boolean("include_reservations").notNull().default(true),
  lastSentAt: timestamp("last_sent_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDailyReportSettingsSchema = createInsertSchema(dailyReportSettings).omit({ id: true, lastSentAt: true, createdAt: true });
export type DailyReportSettings = typeof dailyReportSettings.$inferSelect;
export type InsertDailyReportSettings = z.infer<typeof insertDailyReportSettingsSchema>;

// === INVENTORY TEMPLATES (org-level item definitions) ===
export const inventoryTemplates = pgTable("inventory_templates", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  name: text("name").notNull(),
  zone: text("zone").notNull().default("other"),
  defaultQuantity: integer("default_quantity").notNull().default(1),
  unitCost: numeric("unit_cost"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertInventoryTemplateSchema = createInsertSchema(inventoryTemplates).omit({ id: true, createdAt: true });
export type InventoryTemplate = typeof inventoryTemplates.$inferSelect;
export type InsertInventoryTemplate = z.infer<typeof insertInventoryTemplateSchema>;

// === APARTMENT INVENTORY (per-apartment items) ===
export const apartmentInventory = pgTable("apartment_inventory", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  templateId: integer("template_id").references(() => inventoryTemplates.id, { onDelete: "cascade" }).notNull(),
  quantity: integer("quantity").notNull().default(1),
  condition: text("condition").notNull().default("good"),
  zone: text("zone").notNull().default("other"),
  notes: text("notes"),
  cost: numeric("cost"),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertApartmentInventorySchema = createInsertSchema(apartmentInventory).omit({ id: true, updatedAt: true, createdAt: true });
export type ApartmentInventoryItem = typeof apartmentInventory.$inferSelect;
export type InsertApartmentInventoryItem = z.infer<typeof insertApartmentInventorySchema>;

// === INVENTORY REPORTS (from cleaning jobs) ===
export const inventoryReports = pgTable("inventory_reports", {
  id: serial("id").primaryKey(),
  apartmentId: integer("apartment_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  cleaningJobId: integer("cleaning_job_id").references(() => cleaningJobs.id, { onDelete: "set null" }),
  reportedByUserId: integer("reported_by_user_id").references(() => users.id, { onDelete: "set null" }),
  items: jsonb("items").$type<{ inventoryItemId: number; templateName: string; zone: string; condition: string; notes?: string }[]>().notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertInventoryReportSchema = createInsertSchema(inventoryReports).omit({ id: true, createdAt: true });
export type InventoryReport = typeof inventoryReports.$inferSelect;
export type InsertInventoryReport = z.infer<typeof insertInventoryReportSchema>;

export const INVENTORY_ZONES = [
  "kitchen", "bathroom", "bedroom", "living_room", "dining_room", "balcony", "laundry", "hallway", "other"
] as const;

export const INVENTORY_CONDITIONS = [
  "good", "damaged", "needs_replacement", "missing"
] as const;

// === CONTRACTS (e-signature templates) ===
export const contracts = pgTable("contracts", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  title: text("title").notNull(),
  body: text("body").notNull(),
  fields: jsonb("fields").$type<{ id: string; label: string; type: "text" | "name" | "date" | "signature"; required: boolean; placeholder?: string }[]>().notNull().default([]),
  isActive: boolean("is_active").notNull().default(true),
  showLogo: boolean("show_logo").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertContractSchema = createInsertSchema(contracts).omit({ id: true, createdAt: true });
export type Contract = typeof contracts.$inferSelect;
export type InsertContract = z.infer<typeof insertContractSchema>;

export type ContractSigner = {
  order: number;
  name: string;
  email: string;
  token: string;
  status: "waiting" | "pending" | "signed";
  signedAt?: string | null;
};

// === CONTRACT SESSIONS (individual signing instances) ===
export const contractSessions = pgTable("contract_sessions", {
  id: serial("id").primaryKey(),
  contractId: integer("contract_id").references(() => contracts.id, { onDelete: "cascade" }).notNull(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  token: text("token").notNull().unique(),
  guestName: text("guest_name"),
  guestEmail: text("guest_email"),
  recipientUserId: integer("recipient_user_id").references(() => users.id, { onDelete: "set null" }),
  reservationId: integer("reservation_id").references(() => reservations.id, { onDelete: "set null" }),
  status: text("status").notNull().default("pending"),
  fieldValues: jsonb("field_values").$type<Record<string, string | boolean>>().notNull().default({}),
  signatureImageData: text("signature_image_data"),
  signedAt: timestamp("signed_at"),
  expiresAt: timestamp("expires_at"),
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").defaultNow(),
  isMultiSigner: boolean("is_multi_signer").default(false),
  signers: jsonb("signers").$type<ContractSigner[]>().default([]),
});

export const insertContractSessionSchema = createInsertSchema(contractSessions).omit({ id: true, createdAt: true });
export type ContractSession = typeof contractSessions.$inferSelect;
export type InsertContractSession = z.infer<typeof insertContractSessionSchema>;

// === CONTRACT AUDIT EVENTS ===
export const contractAuditEvents = pgTable("contract_audit_events", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").references(() => contractSessions.id, { onDelete: "cascade" }).notNull(),
  event: text("event").notNull(),
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
});

export const insertContractAuditEventSchema = createInsertSchema(contractAuditEvents).omit({ id: true });
export type ContractAuditEvent = typeof contractAuditEvents.$inferSelect;
export type InsertContractAuditEvent = z.infer<typeof insertContractAuditEventSchema>;

// === MAINTENANCE ESTIMATES ===
export const maintenanceEstimates = pgTable("maintenance_estimates", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  maintenanceJobId: integer("maintenance_job_id").references(() => maintenanceJobs.id, { onDelete: "cascade" }).notNull(),
  ownerUserId: integer("owner_user_id").references(() => users.id, { onDelete: "set null" }),
  estimateNumber: text("estimate_number").notNull(),
  docType: text("doc_type").notNull().default("estimate"),
  status: text("status").notNull().default("draft"),
  lineItems: jsonb("line_items").$type<Array<{
    description: string;
    quantity: number;
    unitCost: string;
    total: string;
  }>>().notNull().default([]),
  subtotal: numeric("subtotal", { precision: 10, scale: 2 }).notNull().default("0.00"),
  notes: text("notes"),
  sentAt: timestamp("sent_at"),
  sentTo: text("sent_to"),
  recipientSnapshot: jsonb("recipient_snapshot").$type<{
    contactName?: string;
    companyName?: string;
    email?: string;
    phone?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }>(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertMaintenanceEstimateSchema = createInsertSchema(maintenanceEstimates).omit({ id: true, createdAt: true });
export type MaintenanceEstimate = typeof maintenanceEstimates.$inferSelect;
export type InsertMaintenanceEstimate = z.infer<typeof insertMaintenanceEstimateSchema>;

// === CLEANING JOB TYPES (custom per-org job type catalog) ===
export const cleaningJobTypes = pgTable("cleaning_job_types", {
  id: serial("id").primaryKey(),
  organizationId: integer("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  name: text("name").notNull(),
  value: text("value").notNull(),
  isDefault: boolean("is_default").default(false),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCleaningJobTypeSchema = createInsertSchema(cleaningJobTypes).omit({ id: true, createdAt: true });
export type CleaningJobType = typeof cleaningJobTypes.$inferSelect;
export type InsertCleaningJobType = z.infer<typeof insertCleaningJobTypeSchema>;

// === QA VISION ===

export type QaIssueJson = {
  description: string;
  severity: 'low' | 'medium' | 'high';
  confidence: 'low' | 'medium' | 'high';
  category: 'cleanliness' | 'staging' | 'amenities' | 'condition';
};

export type QaCriteriaJson = {
  requiredItems?: string[];
  cleanlinessNotes?: string;
  stagingNotes?: string;
  amenityNotes?: string;
  conditionThreshold?: 'pass_all' | 'pass_major';
};

export const qaInspections = pgTable("qa_inspections", {
  id: serial("id").primaryKey(),
  jobId: integer("job_id").references(() => cleaningJobs.id, { onDelete: "cascade" }).notNull(),
  unitId: integer("unit_id").references(() => apartments.id, { onDelete: "set null" }),
  orgId: integer("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  cleanerId: integer("cleaner_id").references(() => users.id, { onDelete: "set null" }),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  overallStatus: text("overall_status").notNull().default("pending"), // pending | in_progress | passed | failed
  overallScore: integer("overall_score"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertQaInspectionSchema = createInsertSchema(qaInspections).omit({ id: true, createdAt: true, updatedAt: true });
export type QaInspection = typeof qaInspections.$inferSelect;
export type InsertQaInspection = z.infer<typeof insertQaInspectionSchema>;

export const qaZoneResults = pgTable("qa_zone_results", {
  id: serial("id").primaryKey(),
  inspectionId: integer("inspection_id").references(() => qaInspections.id, { onDelete: "cascade" }).notNull(),
  zoneName: text("zone_name").notNull(),
  zoneOrder: integer("zone_order").notNull().default(0),
  status: text("status").notNull().default("pending"), // pending | scanning | passed | needs_attention | failed
  cleanlinessScore: integer("cleanliness_score"),
  stagingScore: integer("staging_score"),
  amenityScore: integer("amenity_score"),
  conditionScore: integer("condition_score"),
  issuesJson: jsonb("issues_json").$type<QaIssueJson[]>().default([]),
  photoUrlsJson: jsonb("photo_urls_json").$type<string[]>().default([]),
  apiResponseJson: jsonb("api_response_json"),
  scannedAt: timestamp("scanned_at"),
  scanCount: integer("scan_count").notNull().default(0),
  fixGuidance: text("fix_guidance"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertQaZoneResultSchema = createInsertSchema(qaZoneResults).omit({ id: true, createdAt: true });
export type QaZoneResult = typeof qaZoneResults.$inferSelect;
export type InsertQaZoneResult = z.infer<typeof insertQaZoneResultSchema>;

export const qaCriteria = pgTable("qa_criteria", {
  id: serial("id").primaryKey(),
  unitId: integer("unit_id").references(() => apartments.id, { onDelete: "cascade" }).notNull(),
  orgId: integer("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  zoneName: text("zone_name").notNull(),
  criteriaJson: jsonb("criteria_json").$type<QaCriteriaJson>(),
  referencePhotosJson: jsonb("reference_photos_json").$type<string[]>().default([]),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertQaCriteriaSchema = createInsertSchema(qaCriteria).omit({ id: true, createdAt: true, updatedAt: true });
export type QaCriteria = typeof qaCriteria.$inferSelect;
export type InsertQaCriteria = z.infer<typeof insertQaCriteriaSchema>;

export const qaIssues = pgTable("qa_issues", {
  id: serial("id").primaryKey(),
  zoneResultId: integer("zone_result_id").references(() => qaZoneResults.id, { onDelete: "cascade" }).notNull(),
  description: text("description").notNull(),
  severity: text("severity").notNull().default("medium"), // low | medium | high
  confidence: text("confidence").notNull().default("medium"), // low | medium | high
  category: text("category").notNull(), // cleanliness | staging | amenities | condition
  autoMaintenanceTaskId: integer("auto_maintenance_task_id").references(() => maintenanceJobs.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertQaIssueSchema = createInsertSchema(qaIssues).omit({ id: true, createdAt: true });
export type QaIssue = typeof qaIssues.$inferSelect;
export type InsertQaIssue = z.infer<typeof insertQaIssueSchema>;

export type QaZoneTemplateZone = {
  zoneName: string;
  zoneOrder: number;
  criteria: QaCriteriaJson | null;
};

export const qaZoneTemplates = pgTable("qa_zone_templates", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  name: text("name").notNull(),
  zonesJson: jsonb("zones_json").$type<QaZoneTemplateZone[]>().default([]),
  isDefault: boolean("is_default").default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertQaZoneTemplateSchema = createInsertSchema(qaZoneTemplates).omit({ id: true, createdAt: true, updatedAt: true });
export type QaZoneTemplate = typeof qaZoneTemplates.$inferSelect;
export type InsertQaZoneTemplate = z.infer<typeof insertQaZoneTemplateSchema>;

export const qaOrgSettings = pgTable("qa_org_settings", {
  id: serial("id").primaryKey(),
  orgId: integer("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull().unique(),
  passThreshold: integer("pass_threshold").notNull().default(75),
  rescanLimit: integer("rescan_limit").notNull().default(3),
  requireAllZones: boolean("require_all_zones").notNull().default(true),
  allowOverrideFailed: boolean("allow_override_failed").notNull().default(true),
  autoMaintenance: boolean("auto_maintenance").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertQaOrgSettingsSchema = createInsertSchema(qaOrgSettings).omit({ id: true, createdAt: true, updatedAt: true });
export type QaOrgSettings = typeof qaOrgSettings.$inferSelect;
export type InsertQaOrgSettings = z.infer<typeof insertQaOrgSettingsSchema>;

export const MODULE_KEYS = FEATURE_KEYS;
export type ModuleKey = FeatureKey;

// Re-export chat models for OpenAI integration
export * from "./models/chat";

// Request types
export type CreateSubmissionRequest = {
  guestName: string;
  guestPhone: string;
  guestEmail?: string;
  fobFrontUrl?: string;
  fobBackUrl?: string;
  checklistResults: { itemId: number; isChecked: boolean }[];
  photoResults: { requirementId: number; urls: string[] }[];
};
